<!-- ============================================================
     Manage Transactions — espace d'administration.

     Coque calquée sur celle d'earlychopandyamo (features/admin/
     admin-layout) : barre latérale sombre, contenu à droite, tiroir
     coulissant sous 1024px.

     Toute la page vit sous `.mtx`, et les modales (rendues hors du DOM
     du composant par BsModalService) sous `.mtx-modal`, passé via
     ModalOptions.class. Ce cloisonnement est OBLIGATOIRE : le composant
     est en ViewEncapsulation.None, ses styles sont donc injectés
     globalement et un sélecteur nu (`.btn`, `.card`…) casserait
     Bootstrap sur tout le reste du site.
     ============================================================ -->
<div class="mtx">

  <!-- ═══════════ Barre supérieure (mobile) ═══════════
       La navigation passe par un <select> natif : le sélecteur est rendu par
       le système (roue iOS, liste Android), donc il s'affiche par-dessus tout
       sans dépendre d'un position:fixed ni d'un empilement de z-index — ce
       qu'un tiroir coulissant ne peut pas garantir dans cette page, posée
       sous la coque et les feuilles de style du site public. -->
  <header class="mtx__top">
    <div class="mtx__top-row">
      <span class="mtx__top-brand">Early Transfert &mdash; Admin</span>

      <span class="mtx__top-alert" role="status" *ngIf="totalPending > 0">
        {{totalPending}} à traiter
      </span>
    </div>

    <div class="mtx__top-row">
      <label class="mtx__nav-select-label" for="mtxNavSelect">Écran</label>
      <select id="mtxNavSelect" class="mtx__nav-select"
              [value]="activeTab"
              (change)="switchTab($any($event.target).value)">
        <option *ngFor="let link of navLinks" [value]="link.tab">
          {{link.icon}}  {{link.label}}{{ badgeCount(link) > 0 ? '  (' + badgeCount(link) + ')' : '' }}
        </option>
      </select>

      <button type="button" class="mtx__top-btn" (click)="backToSite()"
              title="Retour au site">← Site</button>
      <button type="button" class="mtx__top-btn mtx__top-btn--out" (click)="logout()"
              title="Déconnexion">Quitter</button>
    </div>
  </header>

  <!-- ═══════════ Navigation latérale (à partir de 1024px) ═══════════ -->
  <nav id="mtx-nav" class="mtx__nav" aria-label="Sections de l'administration">
    <div class="mtx__nav-brand">
      <span class="mtx__nav-mark" aria-hidden="true">⇄</span>
      <span class="mtx__nav-name">Early Transfert</span>
    </div>

    <ul class="mtx__links">
      <li *ngFor="let link of navLinks">
        <button type="button"
                [class.is-active]="activeTab === link.tab"
                [attr.aria-current]="activeTab === link.tab ? 'page' : null"
                (click)="switchTab(link.tab)">
          <span class="mtx__icon" aria-hidden="true">{{link.icon}}</span>
          <span class="mtx__link-label">{{link.label}}</span>
          <span class="mtx__badge" *ngIf="badgeCount(link) > 0">{{badgeCount(link)}}</span>
        </button>
      </li>
    </ul>

    <div class="mtx__nav-foot">
      <button type="button" class="mtx__site" (click)="backToSite()">
        ← Retour au site
      </button>
      <button type="button" class="mtx__logout" (click)="logout()">
        Déconnexion
      </button>
    </div>
  </nav>

  <!-- ═══════════ Contenu ═══════════ -->
  <main class="mtx__content">

    <header class="mtx__head">
      <div>
        <h1 class="mtx__title">{{pageTitle}}</h1>
        <p class="mtx__subtitle">{{pageSubtitle}}</p>
      </div>

      <button type="button" class="mtx__head-alert" *ngIf="totalPending > 0"
              (click)="switchTab('dashboard')">
        {{totalPending}} élément(s) à traiter
      </button>
    </header>

    <!-- ═══════════ Tableau de bord ═══════════ -->
    <div *ngIf="activeTab === 'dashboard'">

      <!-- Les indicateurs couvrent huit collections : les charger à
           l'ouverture de la page ferait payer à chaque visite le coût de
           l'espace entier. On les demande explicitement. -->
      <div class="mtx-callout mtx-callout--cta" *ngIf="!statsLoaded">
        <div>
          <p class="mtx-callout__title">Indicateurs non chargés</p>
          <p class="mtx-callout__hint">
            Les écrans se chargent un par un, à l'ouverture. Cliquez pour lire
            maintenant l'ensemble des collections et remplir le tableau de bord.
          </p>
        </div>
        <button type="button" class="mtx-btn mtx-btn--primary mtx-btn--sm"
                (click)="loadDashboardStats()">
          Charger les indicateurs
        </button>
      </div>

      <div class="mtx-stats">
        <article class="mtx-stat mtx-stat--gold">
          <p class="mtx-stat__label">Solde disponible</p>
          <p class="mtx-stat__value">{{amount | number:'1.0-3'}} <span class="mtx-stat__unit">XAF</span></p>
          <button type="button" class="mtx-stat__action" (click)="openModal(templateSolde)">
            Modifier
          </button>
        </article>

        <article class="mtx-stat">
          <p class="mtx-stat__label">Transactions</p>
          <p class="mtx-stat__value">{{totalTransactions}}</p>
          <p class="mtx-stat__foot">{{completedTransactions}} complétée(s)</p>
        </article>

        <article class="mtx-stat">
          <p class="mtx-stat__label">Portefeuilles</p>
          <p class="mtx-stat__value">{{walletCount}}</p>
        </article>

        <article class="mtx-stat">
          <p class="mtx-stat__label">KYC vérifiés</p>
          <p class="mtx-stat__value">{{verifiedKycCount}}</p>
          <p class="mtx-stat__foot" *ngIf="pendingKycCount > 0">
            {{pendingKycCount}} en attente
          </p>
        </article>
      </div>

      <div class="mtx-dash">
        <!-- À traiter -->
        <section class="mtx-panel">
          <div class="mtx-panel__body">
            <h2 class="mtx-panel__title">À traiter</h2>

            <ul class="mtx-queue" *ngIf="statsLoaded">
              <li class="mtx-queue__item" *ngFor="let queue of pendingQueues">
                <span class="mtx-queue__label">{{queue.label}}</span>
                <span class="mtx-queue__count"
                      [ngClass]="{'mtx-queue__count--zero': queue.count === 0}">{{queue.count}}</span>
                <button type="button" class="mtx-linkbtn" (click)="switchTab(queue.tab)">
                  Ouvrir →
                </button>
              </li>
            </ul>

            <!-- Avant chargement tous les compteurs valent 0 : dix lignes de
                 « 0 » affirmeraient « rien en attente » alors que la vérité est
                 « rien n'est chargé ». D'où la condition sur statsLoaded. -->
            <p class="mtx-empty" *ngIf="!statsLoaded">
              Chargez les indicateurs pour voir les files d'attente.
            </p>
          </div>
        </section>

        <!-- Soldes des portefeuilles -->
        <section class="mtx-panel">
          <div class="mtx-panel__body">
            <h2 class="mtx-panel__title">Encours des portefeuilles</h2>

            <ul class="mtx-queue" *ngIf="walletTotals.length > 0">
              <li class="mtx-queue__item" *ngFor="let total of walletTotals">
                <span class="mtx-queue__label">{{total.currency}}</span>
                <span class="mtx-queue__amount">{{total.total | number:'1.0-2'}}</span>
              </li>
            </ul>

            <p class="mtx-empty" *ngIf="walletTotals.length === 0">
              Aucun portefeuille chargé.
            </p>

            <button type="button" class="mtx-btn mtx-btn--ghost mtx-btn--sm mtx-panel__cta"
                    (click)="switchTab('wallets')">
              Voir les portefeuilles
            </button>
          </div>
        </section>
      </div>
    </div>

    <!-- ═══════════ Filtres (écrans à grille uniquement) ═══════════ -->
    <div class="mtx__filters"
         *ngIf="activeTab !== 'dashboard' && activeTab !== 'fees' && activeTab !== 'rates' && activeTab !== 'settings'">
      <div class="mtx__filter mtx__filter--grow">
        <label class="mtx__filter-label" for="mtxEmailFilter">Email</label>
        <input id="mtxEmailFilter" type="text" class="mtx-input"
               [(ngModel)]="emailFilter"
               (ngModelChange)="filterByEmail()"
               placeholder="Filtrer par email…">
      </div>

      <div class="mtx__filter">
        <label class="mtx__filter-label" for="mtxDateFrom">Du</label>
        <input id="mtxDateFrom" type="date" class="mtx-input mtx-input--date"
               [(ngModel)]="dateFrom"
               (ngModelChange)="filterByDate()">
      </div>

      <div class="mtx__filter">
        <label class="mtx__filter-label" for="mtxDateTo">Au</label>
        <input id="mtxDateTo" type="date" class="mtx-input mtx-input--date"
               [(ngModel)]="dateTo"
               (ngModelChange)="filterByDate()">
      </div>

      <button class="mtx-btn mtx-btn--ghost mtx-btn--sm" (click)="clearDateFilter()"
              *ngIf="dateFrom || dateTo">
        Effacer les dates
      </button>
    </div>

    <!-- ═══════════ Transactions ═══════════ -->
    <div *ngIf="activeTab === 'transactions'">
      <div class="mtx__toolbar">
        <div class="mtx__segment" role="group" aria-label="Origine des transactions">
          <button type="button" class="mtx__segment-btn"
                  [class.is-active]="transactionTypeFilter === 'all'"
                  (click)="setTransactionTypeFilter('all')">
            Toutes <span class="mtx__segment-count">{{marketplaceCount + directCount}}</span>
          </button>
          <button type="button" class="mtx__segment-btn"
                  [class.is-active]="transactionTypeFilter === 'marketplace'"
                  (click)="setTransactionTypeFilter('marketplace')">
            Marketplace <span class="mtx__segment-count">{{marketplaceCount}}</span>
          </button>
          <button type="button" class="mtx__segment-btn"
                  [class.is-active]="transactionTypeFilter === 'direct'"
                  (click)="setTransactionTypeFilter('direct')">
            Transfert direct <span class="mtx__segment-count">{{directCount}}</span>
          </button>
        </div>

        <button type="button" class="mtx-btn mtx-btn--ghost mtx-btn--sm"
                *ngIf="transactionTypeFilter === 'marketplace' && postGroupCount > 0"
                (click)="hasExpandedPost ? collapseAllPosts() : expandAllPosts()">
          {{ hasExpandedPost ? 'Tout replier' : 'Tout déplier' }}
        </button>
      </div>

      <p class="mtx__note" *ngIf="transactionTypeFilter === 'marketplace'">
        {{postGroupCount}} post(s) — cliquez une ligne de post pour dérouler ses
        transactions. « Voir le post » ouvre le post, la proposition acceptée
        et les deux transactions liées.
      </p>
      <p class="mtx__note" *ngIf="transactionTypeFilter !== 'marketplace'">
        La colonne Origine distingue les transactions issues d'un post marketplace
        des transferts directs.
      </p>

      <div *ngIf="showXl" class="mtx__grid">
        <ag-grid-angular
          [enableCellTextSelection]="true"
          [ensureDomOrder]="true"
          style="width: 100%; height: 1000px;"
          class="ag-theme-balham custom-class"
          [defaultColDef]="defaultColDef"
          [columnDefs]="columnDefs"
          [rowData]="transactionsVisibleRows"
          [isFullWidthCell]="isFullWidthCell"
          [fullWidthCellRenderer]="fullWidthCellRenderer"
          [animateRows]="true"
          [pagination]="true"
          [frameworkComponents]="frameworkComponents"
          [isExternalFilterPresent]="isExternalFilterPresent"
          [doesExternalFilterPass]="doesExternalFilterPass"
          (selectionChanged)="onSelectionChanged($event)"
          (gridReady)="onGridReady($event)"
          >
        </ag-grid-angular>
      </div>

      <div *ngIf="showXs" class="mtx__grid">
        <ag-grid-angular
          [enableCellTextSelection]="true"
          [ensureDomOrder]="true"
          style="width: 100%; height: 1000px;"
          class="ag-theme-balham custom-class"
          [defaultColDef]="defaultColDef"
          [columnDefs]="columnDefsXs"
          [rowData]="transactionsVisibleRows"
          [isFullWidthCell]="isFullWidthCell"
          [fullWidthCellRenderer]="fullWidthCellRenderer"
          [animateRows]="true"
          [pagination]="true"
          [frameworkComponents]="frameworkComponents"
          [isExternalFilterPresent]="isExternalFilterPresent"
          [doesExternalFilterPass]="doesExternalFilterPass"
          (selectionChanged)="onSelectionChanged($event)"
          (gridReady)="onGridReady($event)"
          >
        </ag-grid-angular>
      </div>
    </div>

    <!-- ═══════════ KYC ═══════════ -->
    <div *ngIf="activeTab === 'kyc'">

      <div class="mtx__toolbar">
        <button class="mtx-btn mtx-btn--primary" (click)="openKycDetailModal(templateKycDetail, selectedKyc)"
                [disabled]="!selectedKyc">
          Voir le détail
        </button>
        <button class="mtx-btn mtx-btn--success" (click)="approveKyc(selectedKyc)"
                [disabled]="!selectedKyc || selectedKyc?.status !== 'pending'">
          Approuver
        </button>
        <button class="mtx-btn mtx-btn--danger" (click)="openRejectModal(templateReject, selectedKyc)"
                [disabled]="!selectedKyc || selectedKyc?.status !== 'pending'">
          Rejeter
        </button>
        <button class="mtx-btn mtx-btn--ghost" (click)="setKycPending(selectedKyc)"
                [disabled]="!selectedKyc || selectedKyc?.status === 'pending'">
          Remettre en attente
        </button>
      </div>
      <p class="mtx__hint">
        <span *ngIf="!selectedKyc">Cliquez une ligne pour sélectionner une vérification</span>
        <span *ngIf="selectedKyc">Sélection : <strong>{{selectedKyc.userEmail}}</strong> ({{selectedKyc.status | uppercase}})</span>
      </p>

      <div *ngIf="showXl" class="mtx__grid">
        <ag-grid-angular
          [enableCellTextSelection]="true"
          [ensureDomOrder]="true"
          style="width: 100%; height: 800px;"
          class="ag-theme-balham custom-class"
          [defaultColDef]="defaultColDef"
          [columnDefs]="kycColumnDefs"
          [rowData]="kycRowData"
          [animateRows]="true"
          [pagination]="true"
          [rowSelection]="'single'"
          [isExternalFilterPresent]="isExternalFilterPresent"
          [doesExternalFilterPass]="doesExternalFilterPass"
          (rowClicked)="onKycRowClicked($event)"
          (rowDoubleClicked)="onKycRowDoubleClicked($event)"
          (gridReady)="onKycGridReady($event)"
          >
        </ag-grid-angular>
      </div>

      <div *ngIf="showXs" class="mtx__grid">
        <ag-grid-angular
          [enableCellTextSelection]="true"
          [ensureDomOrder]="true"
          style="width: 100%; height: 800px;"
          class="ag-theme-balham custom-class"
          [defaultColDef]="defaultColDef"
          [columnDefs]="kycColumnDefsXs"
          [rowData]="kycRowData"
          [animateRows]="true"
          [pagination]="true"
          [rowSelection]="'single'"
          [isExternalFilterPresent]="isExternalFilterPresent"
          [doesExternalFilterPass]="doesExternalFilterPass"
          (rowClicked)="onKycRowClicked($event)"
          (rowDoubleClicked)="onKycRowDoubleClicked($event)"
          (gridReady)="onKycGridReady($event)"
          >
        </ag-grid-angular>
      </div>
    </div>

    <!-- ═══════════ Conformité ═══════════ -->
    <div *ngIf="activeTab === 'compliance'">

      <div class="mtx__toolbar">
        <button class="mtx-btn mtx-btn--primary"
                (click)="openComplianceDetailModal(templateComplianceDetail, selectedCompliance)"
                [disabled]="!selectedCompliance">
          Voir le dossier
        </button>
        <button class="mtx-btn mtx-btn--success" (click)="approveCompliance(selectedCompliance)"
                [disabled]="!selectedCompliance || selectedCompliance?.status !== 'pending'">
          Approuver
        </button>
        <button class="mtx-btn mtx-btn--danger"
                (click)="openComplianceRejectModal(templateComplianceReject, selectedCompliance)"
                [disabled]="!selectedCompliance || selectedCompliance?.status !== 'pending'">
          Rejeter
        </button>
      </div>
      <p class="mtx__hint">
        <span *ngIf="!selectedCompliance">Cliquez une ligne pour sélectionner un dossier</span>
        <span *ngIf="selectedCompliance">
          Sélection : <strong>{{selectedCompliance.userEmail}}</strong>
          — {{selectedCompliance.amount}} {{selectedCompliance.currency}}
          ({{selectedCompliance.status | uppercase}})
        </span>
      </p>
      <p class="mtx__hint">
        Approuver un transfert direct <strong>exécute le débit immédiatement</strong>. Le solde,
        les frais et le taux sont réévalués à cet instant : si l'usager a dépensé l'argent
        entre-temps, l'opération échoue proprement et le dossier passe en « FAILED » avec le motif.
      </p>

      <div class="mtx__grid">
        <ag-grid-angular
          [enableCellTextSelection]="true"
          [ensureDomOrder]="true"
          style="width: 100%; height: 800px;"
          class="ag-theme-balham custom-class"
          [defaultColDef]="defaultColDef"
          [columnDefs]="complianceColumnDefs"
          [rowData]="complianceRowData"
          [animateRows]="true"
          [pagination]="true"
          [rowSelection]="'single'"
          (rowClicked)="onComplianceRowClicked($event)"
          (rowDoubleClicked)="onComplianceRowDoubleClicked($event)"
          (gridReady)="onComplianceGridReady($event)"
          >
        </ag-grid-angular>
      </div>
    </div>

    <!-- ═══════════ Messages ═══════════ -->
    <div *ngIf="activeTab === 'messages'">

      <div class="mtx__toolbar">
        <button class="mtx-btn mtx-btn--primary" (click)="openAddMessageModal(templateAddMessage)">
          + Nouveau message
        </button>
        <button class="mtx-btn mtx-btn--ghost" (click)="openAddMessageModal(templateAddMessage, selectedMessage)"
                [disabled]="!selectedMessage">
          Modifier
        </button>
        <button class="mtx-btn mtx-btn--danger" (click)="deleteMessage()"
                [disabled]="!selectedMessage">
          Supprimer
        </button>
        <button class="mtx-btn mtx-btn--ghost" (click)="toggleMessageActive()"
                [disabled]="!selectedMessage">
          Activer / désactiver
        </button>
      </div>
      <p class="mtx__hint">
        <span *ngIf="!selectedMessage">Cliquez une ligne pour sélectionner un message</span>
        <span *ngIf="selectedMessage">Sélection : « {{selectedMessage.messageEn | slice:0:40}}… » ({{selectedMessage.isActive ? 'Actif' : 'Inactif'}}) → {{selectedMessage.targetUserEmail || 'Tous les utilisateurs'}}</span>
      </p>

      <div class="mtx__grid">
        <ag-grid-angular
          [enableCellTextSelection]="true"
          [ensureDomOrder]="true"
          style="width: 100%; height: 700px;"
          class="ag-theme-balham custom-class"
          [defaultColDef]="defaultColDef"
          [columnDefs]="messagesColumnDefs"
          [rowData]="messagesRowData"
          [animateRows]="true"
          [pagination]="true"
          [rowSelection]="'single'"
          [isExternalFilterPresent]="isExternalFilterPresent"
          [doesExternalFilterPass]="doesExternalFilterPass"
          (rowClicked)="onMessagesRowClicked($event)"
          (gridReady)="onMessagesGridReady($event)">
        </ag-grid-angular>
      </div>
    </div>

    <!-- ═══════════ Portefeuilles ═══════════ -->
    <div *ngIf="activeTab === 'wallets'">

      <div class="mtx__toolbar">
        <button class="mtx-btn mtx-btn--primary" (click)="openEditWalletModal(templateEditWallet, selectedWallet)"
                [disabled]="!selectedWallet">
          Modifier le solde
        </button>
      </div>
      <p class="mtx__hint">
        <span *ngIf="!selectedWallet">Cliquez une ligne pour sélectionner un portefeuille</span>
        <span *ngIf="selectedWallet">
          Sélection : <strong>{{selectedWallet.usersEmail}}</strong> — {{selectedWallet.currency}}
          (solde : {{selectedWallet.amount | number:'1.2-2'}})
        </span>
      </p>

      <div class="mtx__grid">
        <ag-grid-angular
          [enableCellTextSelection]="true"
          [ensureDomOrder]="true"
          style="width: 100%; height: 700px;"
          class="ag-theme-balham custom-class"
          [defaultColDef]="defaultColDef"
          [columnDefs]="walletColumnDefs"
          [rowData]="walletRowData"
          [animateRows]="true"
          [pagination]="true"
          [rowSelection]="'single'"
          [isExternalFilterPresent]="isExternalFilterPresent"
          [doesExternalFilterPass]="doesExternalFilterPass"
          (rowClicked)="onWalletRowClicked($event)"
          (rowDoubleClicked)="openEditWalletModal(templateEditWallet, $event.data)"
          (gridReady)="onWalletGridReady($event)">
        </ag-grid-angular>
      </div>
    </div>

    <!-- ═══════════ Emails Interac ═══════════ -->
    <div *ngIf="activeTab === 'interac'">

      <div class="mtx__toolbar">
        <button class="mtx-btn mtx-btn--primary" (click)="checkInteracEmails()"
                [disabled]="interacChecking">
          <span *ngIf="!interacChecking">Relever les emails</span>
          <span *ngIf="interacChecking">Relève en cours…</span>
        </button>
        <button class="mtx-btn mtx-btn--ghost" (click)="openInteracBodyModal(templateInteracBody)"
                [disabled]="!selectedInteracEmail">
          Voir l'email
        </button>
        <button class="mtx-btn mtx-btn--success" (click)="markInteracProcessed()"
                [disabled]="!selectedInteracEmail || selectedInteracEmail?.status === 'processed'">
          Marquer traité
        </button>
        <button class="mtx-btn mtx-btn--ghost" (click)="markInteracIgnored()"
                [disabled]="!selectedInteracEmail || selectedInteracEmail?.status === 'ignored'">
          Ignorer
        </button>
        <button class="mtx-btn mtx-btn--ghost" (click)="markInteracNew()"
                [disabled]="!selectedInteracEmail || selectedInteracEmail?.status === 'new'">
          Remettre à « nouveau »
        </button>
      </div>
      <p class="mtx__hint">
        <span *ngIf="!selectedInteracEmail">Cliquez une ligne pour sélectionner un email Interac</span>
        <span *ngIf="selectedInteracEmail">
          Sélection : <strong>{{selectedInteracEmail.from}}</strong> — « {{selectedInteracEmail.subject | slice:0:50}} »
          ({{selectedInteracEmail.status | uppercase}})
        </span>
      </p>
      <p class="mtx__note">
        Boîte surveillée : <strong>info&#64;earlytransfert.com</strong> — objets contenant « interac »
      </p>

      <div class="mtx__grid">
        <ag-grid-angular
          [enableCellTextSelection]="true"
          [ensureDomOrder]="true"
          style="width: 100%; height: 700px;"
          class="ag-theme-balham custom-class"
          [defaultColDef]="defaultColDef"
          [columnDefs]="interacColumnDefs"
          [rowData]="interacRowData"
          [animateRows]="true"
          [pagination]="true"
          [rowSelection]="'single'"
          [isExternalFilterPresent]="isExternalFilterPresent"
          [doesExternalFilterPass]="doesExternalFilterPass"
          (rowClicked)="onInteracRowClicked($event)"
          (rowDoubleClicked)="onInteracRowDoubleClicked($event)"
          (gridReady)="onInteracGridReady($event)">
        </ag-grid-angular>
      </div>
    </div>

    <!-- ═══════════ Mouvements d'argent ═══════════ -->
    <div *ngIf="activeTab === 'money_events'">
      <p class="mtx__note">
        Chaque tentative de mouvement d'argent, réussie ou non. Une même opération peut
        apparaître plusieurs fois : la tentative vue par le serveur, le mouvement de solde
        constaté, et l'échec signalé par le client. C'est leur recoupement qui renseigne —
        un « Réussi » sans mouvement, ou un mouvement « Non attribué », sont des anomalies.
      </p>

      <div class="mtx__filters">
        <input type="text" class="mtx-input"
               [(ngModel)]="moneyEventSearch"
               (keyup.enter)="searchMoneyEvents()"
               placeholder="Courriel du client, ou code de transaction…">
        <button class="mtx-btn mtx-btn--primary" (click)="searchMoneyEvents()">
          Rechercher
        </button>
        <button class="mtx-btn mtx-btn--ghost" (click)="loadRecentMoneyEvents()">
          Les plus récents
        </button>
      </div>

      <p class="mtx__hint">
        <span *ngIf="moneyEventLoading">Chargement…</span>
        <span *ngIf="!moneyEventLoading">{{moneyEventNotice}}</span>
      </p>

      <div class="mtx__grid">
        <ag-grid-angular
          [enableCellTextSelection]="true"
          [ensureDomOrder]="true"
          style="width: 100%; height: 700px;"
          class="ag-theme-balham custom-class"
          [defaultColDef]="defaultColDef"
          [columnDefs]="moneyEventColumnDefs"
          [rowData]="moneyEventRowData"
          [animateRows]="true"
          [pagination]="true"
          [rowSelection]="'single'"
          [isExternalFilterPresent]="isExternalFilterPresent"
          [doesExternalFilterPass]="doesExternalFilterPass"
          (rowClicked)="onMoneyEventRowClicked($event)"
          (gridReady)="onMoneyEventGridReady($event)">
        </ag-grid-angular>
      </div>

    </div>

    <div *ngIf="activeTab === 'momo'">

      <div class="mtx__toolbar">
        <button class="mtx-btn mtx-btn--success" (click)="approveMomoDeposit()"
                [disabled]="!selectedMomoDeposit || selectedMomoDeposit?.status === 'approved'">
          Approuver &amp; créditer
        </button>
        <button class="mtx-btn mtx-btn--danger" (click)="rejectMomoDeposit()"
                [disabled]="!selectedMomoDeposit || selectedMomoDeposit?.status === 'rejected'">
          Rejeter
        </button>
      </div>
      <p class="mtx__hint">
        <span *ngIf="!selectedMomoDeposit">Cliquez une ligne pour sélectionner une déclaration de dépôt</span>
        <span *ngIf="selectedMomoDeposit">
          Sélection : <strong>{{selectedMomoDeposit.walletEmail}}</strong> — {{selectedMomoDeposit.operator}} —
          {{selectedMomoDeposit.amount | number}} {{selectedMomoDeposit.currency || 'XAF'}}
          (TX : {{selectedMomoDeposit.transactionId}} — {{selectedMomoDeposit.status | uppercase}})
        </span>
      </p>
      <p class="mtx__note">
        Cameroun — déclarations de dépôt Mobile Money soumises par les clients, en attente de vérification
      </p>

      <div class="mtx__grid">
        <ag-grid-angular
          [enableCellTextSelection]="true"
          [ensureDomOrder]="true"
          style="width: 100%; height: 700px;"
          class="ag-theme-balham custom-class"
          [defaultColDef]="defaultColDef"
          [columnDefs]="momoColumnDefs"
          [rowData]="momoRowData"
          [animateRows]="true"
          [pagination]="true"
          [rowSelection]="'single'"
          [isExternalFilterPresent]="isExternalFilterPresent"
          [doesExternalFilterPass]="doesExternalFilterPass"
          (rowClicked)="onMomoRowClicked($event)"
          (gridReady)="onMomoGridReady($event)">
        </ag-grid-angular>
      </div>
    </div>

    <!-- ═══════════ Dépôts bancaires (GB / FR / CN) ═══════════ -->
    <div *ngIf="activeTab === 'bank_deposits'">

      <div class="mtx__toolbar">
        <button class="mtx-btn mtx-btn--success" (click)="approveBankDeposit()"
                [disabled]="!selectedBankDeposit || selectedBankDeposit?.status === 'approved'">
          Approuver &amp; créditer
        </button>
        <button class="mtx-btn mtx-btn--danger" (click)="rejectBankDeposit()"
                [disabled]="!selectedBankDeposit || selectedBankDeposit?.status !== 'pending'">
          Rejeter
        </button>
      </div>

      <p class="mtx__hint">
        <span *ngIf="!selectedBankDeposit">Cliquez une ligne pour sélectionner une déclaration de virement</span>
        <span *ngIf="selectedBankDeposit">
          Sélection : <strong>{{selectedBankDeposit.walletEmail}}</strong> —
          {{selectedBankDeposit.countryCode}} — {{selectedBankDepositLabel}}
          (réf. : {{selectedBankDeposit.reference}} — {{selectedBankDeposit.status | uppercase}})
        </span>
      </p>

      <p class="mtx__note">
        Royaume-Uni, France, Chine — virements déclarés par les clients. <strong>Vérifiez le
        virement sur le relevé du compte de réception avant d'approuver</strong> : rien n'est
        automatique sur ce rail, la référence portée par le client est le seul lien entre
        l'argent reçu et le compte à créditer.
      </p>

      <div class="mtx__grid">
        <ag-grid-angular
          [enableCellTextSelection]="true"
          [ensureDomOrder]="true"
          style="width: 100%; height: 700px;"
          class="ag-theme-balham custom-class"
          [defaultColDef]="defaultColDef"
          [columnDefs]="bankDepositColumnDefs"
          [rowData]="bankDepositRowData"
          [animateRows]="true"
          [pagination]="true"
          [rowSelection]="'single'"
          [isExternalFilterPresent]="isExternalFilterPresent"
          [doesExternalFilterPass]="doesExternalFilterPass"
          (rowClicked)="onBankDepositRowClicked($event)"
          (gridReady)="onBankDepositGridReady($event)">
        </ag-grid-angular>
      </div>
    </div>

    <!-- ═══════════ Marketplace ═══════════ -->
    <div *ngIf="activeTab === 'marketplace'">

      <p class="mtx__note">
        Tous les posts publiés, les offres reçues sur chacun, et les frais encaissés par la
        plateforme. Dépliez un post pour voir ses offres, puis une offre acceptée pour voir
        ses <strong>deux transactions</strong> (propriétaire du post et offreur).
      </p>
      <p class="mtx__hint">
        <strong>Bénéfices cumulés (tous posts) : {{marketplaceTotals}}</strong>
        <span> — les devises ne sont jamais additionnées entre elles.</span>
      </p>

      <div class="mtx__toolbar">
        <input class="mtx-input" type="text" [(ngModel)]="marketplaceFilter"
               (ngModelChange)="onMarketplaceFilterChange()"
               placeholder="Rechercher : auteur, corridor, offreur…" />
        <select class="mtx-input" [(ngModel)]="marketplaceSort"
                (ngModelChange)="onMarketplaceFilterChange()">
          <option value="offers">Trier : plus d'offres</option>
          <option value="accepted">Trier : plus d'offres acceptées</option>
          <option value="author">Trier : auteur (A→Z)</option>
        </select>
      </div>

      <div class="mtx__toolbar">
        <button class="mtx-btn mtx-btn--ghost mtx-btn--sm" (click)="changeMarketplacePage(-1)"
                [disabled]="marketplacePageIndex === 0">← Page précédente</button>
        <span class="mtx__hint">
          {{filteredMarketplaceRows.length}} post(s) — page {{marketplacePageIndex + 1}}
          / {{marketplacePageCount}} ({{marketplacePageSize}} par page)
        </span>
        <button class="mtx-btn mtx-btn--ghost mtx-btn--sm" (click)="changeMarketplacePage(1)"
                [disabled]="marketplacePageIndex >= marketplacePageCount - 1">Page suivante →</button>
      </div>
      <p class="mtx__hint" *ngIf="marketplaceGlobalFilterActive">
        Filtre actif du bandeau (email / dates) appliqué aux posts <em>et</em> aux offres.
      </p>

      <div class="mtx-md">
        <div class="mtx-md__empty" *ngIf="filteredMarketplaceRows.length === 0">
          Aucun post à afficher
        </div>

        <div class="mtx-md__post" *ngFor="let p of pagedMarketplaceRows">

          <!-- ── Niveau 1 : le post ── -->
          <div class="mtx-md__row mtx-md__row--post" (click)="togglePost(p.id)">
            <span class="mtx-md__chevron" [class.is-open]="expandedPostId === p.id">▶</span>
            <span class="mtx-md__cell mtx-md__cell--grow">
              <strong>{{p.author}}</strong><br />
              <small>{{p.corridor}} · {{p.amount}} · taux {{p.rate}}</small>
            </span>
            <span class="mtx-md__cell">
              {{p.offersCount}} offre(s)<br /><small>{{p.acceptedCount}} acceptée(s)</small>
            </span>
            <span class="mtx-md__cell mtx-md__benefit">
              <small>Bénéfices</small><br /><strong>{{p.benefit}}</strong>
            </span>
            <span class="mtx-md__badge">{{p.lifecycle}}</span>
          </div>

          <!-- ── Niveau 2 : les offres du post ── -->
          <div class="mtx-md__panel" *ngIf="expandedPostId === p.id">
            <div class="mtx-md__empty" *ngIf="visibleExpandedOffers.length === 0">Aucune offre sur ce post</div>

            <div *ngFor="let o of visibleExpandedOffers">
              <div class="mtx-md__row mtx-md__row--offer"
                   [class.is-clickable]="o.hasTx" (click)="o.hasTx && toggleOffer(o.id)">
                <span class="mtx-md__chevron" [class.is-open]="expandedOfferId === o.id"
                      [class.is-hidden]="!o.hasTx">▶</span>
                <span class="mtx-md__cell mtx-md__cell--grow">
                  <strong>{{o.name}}</strong><br />
                  <small>{{o.email}}<span *ngIf="o.phone"> · {{o.phone}}</span></small>
                </span>
                <span class="mtx-md__cell">
                  Envoie : {{o.offered || '—'}}<br /><small>Reçoit : {{o.received || '—'}}</small>
                </span>
                <span class="mtx-md__cell">
                  Frais prop. : {{o.ownerFee}}<br /><small>Frais offreur : {{o.bidderFee}}</small>
                </span>
                <span class="mtx-md__cell">
                  Débit prop. : {{o.ownerDebited}}<br /><small>Débit offreur : {{o.bidderDebited}}</small>
                </span>
                <span class="mtx-md__badge"
                      [class.cell-pass]="o.state === 'Acceptée'"
                      [class.cell-fail]="o.state === 'Retirée' || o.state === 'Annulée'"
                      [class.cell-pending]="o.state === 'En attente'">{{o.state}}</span>
              </div>

              <!-- ── Niveau 3 : les 2 transactions de l'offre acceptée ── -->
              <div class="mtx-md__panel mtx-md__panel--tx" *ngIf="expandedOfferId === o.id">
                <div class="mtx-md__tx-card" *ngFor="let t of o.transactions">
                  <div class="mtx-md__tx-head">
                    <strong>{{t.role}}</strong>
                    <span class="mtx-md__badge"
                          [class.cell-pass]="t.status === 'COMPLETE'"
                          [class.cell-inProgress]="t.status === 'INPROGRESS'"
                          [class.cell-pending]="t.status === 'PENDING'">{{t.status}}</span>
                  </div>
                  <div class="mtx-md__kv"><span>Code transaction</span><b>{{t.code}}</b></div>
                  <div class="mtx-md__kv"><span>Client</span><b>{{t.user}}</b></div>
                  <div class="mtx-md__kv"><span>Contrepartie</span><b>{{t.counterparty}}</b></div>
                  <div class="mtx-md__kv"><span>Date</span><b>{{t.when || '—'}}</b></div>
                  <div class="mtx-md__kv"><span>Corridor</span><b>{{t.corridor}}</b></div>
                  <div class="mtx-md__kv"><span>Méthode</span><b>{{t.method || '—'}}</b></div>
                  <div class="mtx-md__kv"><span>Envoie</span><b>{{t.amountSend}}</b></div>
                  <div class="mtx-md__kv"><span>Reçoit</span><b>{{t.amountReceive}}</b></div>
                  <div class="mtx-md__kv"><span>Taux</span><b>{{t.rate || '—'}}</b></div>
                  <div class="mtx-md__kv mtx-md__kv--strong">
                    <span>Frais encaissés</span><b>{{t.fee}}</b>
                  </div>
                  <div class="mtx-md__kv"><span>Total débité</span><b>{{t.totalDebited}}</b></div>
                  <div class="mtx-md__kv"><span>N° expéditeur</span><b>{{t.senderNumber || '—'}}</b></div>
                  <div class="mtx-md__kv"><span>N° bénéficiaire</span><b>{{t.receiverNumber || '—'}}</b></div>
                  <div class="mtx-md__kv"><span>Bénéficiaire</span><b>{{t.receiver || '—'}}</b></div>
                  <div class="mtx-md__kv" *ngIf="t.stages.length">
                    <span>Étapes</span>
                    <b><span *ngFor="let s of t.stages">{{s.label}} : {{s.time}} &nbsp;</span></b>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mtx__toolbar" *ngIf="marketplacePageCount > 1">
        <button class="mtx-btn" (click)="changeMarketplacePage(-1)"
                [disabled]="marketplacePageIndex === 0">← Précédent</button>
        <span class="mtx__hint">Page {{marketplacePageIndex + 1}} / {{marketplacePageCount}}</span>
        <button class="mtx-btn" (click)="changeMarketplacePage(1)"
                [disabled]="marketplacePageIndex >= marketplacePageCount - 1">Suivant →</button>
      </div>
    </div>

    <!-- ═══════════ Signalements ═══════════ -->
    <div *ngIf="activeTab === 'issues'">

      <div class="mtx__toolbar">
        <button class="mtx-btn mtx-btn--primary" (click)="checkIssueReportEmails()"
                [disabled]="issuesChecking">
          <span *ngIf="!issuesChecking">Relever les signalements</span>
          <span *ngIf="issuesChecking">Relève en cours…</span>
        </button>
        <button class="mtx-btn mtx-btn--ghost" (click)="openIssueBodyModal(templateIssueBody)"
                [disabled]="!selectedIssue">
          Voir le signalement
        </button>
        <button class="mtx-btn mtx-btn--success" (click)="markIssueResolved()"
                [disabled]="!selectedIssue || selectedIssue?.status === 'resolved'">
          Marquer résolu
        </button>
        <button class="mtx-btn mtx-btn--ghost" (click)="markIssueIgnored()"
                [disabled]="!selectedIssue || selectedIssue?.status === 'ignored'">
          Ignorer
        </button>
        <button class="mtx-btn mtx-btn--ghost" (click)="markIssueNew()"
                [disabled]="!selectedIssue || selectedIssue?.status === 'new'">
          Remettre à « nouveau »
        </button>
      </div>
      <p class="mtx__hint">
        <span *ngIf="!selectedIssue">Cliquez une ligne pour sélectionner un signalement</span>
        <span *ngIf="selectedIssue">
          Sélection : <strong>{{selectedIssue.from}}</strong> — « {{selectedIssue.subject | slice:0:50}} »
          ({{selectedIssue.status | uppercase}})
        </span>
      </p>
      <p class="mtx__note">
        Boîte surveillée : <strong>info&#64;earlytransfert.com</strong> — objets contenant « Issue Report from »
      </p>

      <div class="mtx__grid">
        <ag-grid-angular
          [enableCellTextSelection]="true"
          [ensureDomOrder]="true"
          style="width: 100%; height: 700px;"
          class="ag-theme-balham custom-class"
          [defaultColDef]="defaultColDef"
          [columnDefs]="issuesColumnDefs"
          [rowData]="issuesRowData"
          [animateRows]="true"
          [pagination]="true"
          [rowSelection]="'single'"
          [isExternalFilterPresent]="isExternalFilterPresent"
          [doesExternalFilterPass]="doesExternalFilterPass"
          (rowClicked)="onIssueRowClicked($event)"
          (rowDoubleClicked)="onIssueRowDoubleClicked($event)"
          (gridReady)="onIssuesGridReady($event)">
        </ag-grid-angular>
      </div>
    </div>

    <!-- ═══════════ Litiges de dépôt ═══════════ -->
    <div *ngIf="activeTab === 'deposit_issues'">

      <div class="mtx__toolbar">
        <div class="mtx__segment" role="group" aria-label="Type de dépôt">
          <button type="button" class="mtx__segment-btn"
                  [class.is-active]="depositTypeFilter === 'all'"
                  (click)="setDepositTypeFilter('all')">Tous</button>
          <button type="button" class="mtx__segment-btn"
                  [class.is-active]="depositTypeFilter === 'interac'"
                  (click)="setDepositTypeFilter('interac')">Interac</button>
          <button type="button" class="mtx__segment-btn"
                  [class.is-active]="depositTypeFilter === 'mobile_money'"
                  (click)="setDepositTypeFilter('mobile_money')">Mobile Money</button>
        </div>
        <button class="mtx-btn mtx-btn--primary" (click)="openDepositIssueBodyModal(templateDepositIssueBody)"
                [disabled]="!selectedDepositIssue">
          Voir le litige
        </button>
        <button class="mtx-btn mtx-btn--success" (click)="markDepositIssueResolved()"
                [disabled]="!selectedDepositIssue || selectedDepositIssue?.status === 'resolved'">
          Marquer résolu
        </button>
        <button class="mtx-btn mtx-btn--ghost" (click)="markDepositIssueIgnored()"
                [disabled]="!selectedDepositIssue || selectedDepositIssue?.status === 'ignored'">
          Ignorer
        </button>
        <button class="mtx-btn mtx-btn--ghost" (click)="markDepositIssueNew()"
                [disabled]="!selectedDepositIssue || selectedDepositIssue?.status === 'new'">
          Remettre à « nouveau »
        </button>
      </div>
      <p class="mtx__hint">
        <span *ngIf="!selectedDepositIssue">Cliquez une ligne pour sélectionner un litige</span>
        <span *ngIf="selectedDepositIssue">
          Sélection : <strong>{{selectedDepositIssue.userEmail}}</strong> —
          {{selectedDepositIssue.depositType === 'interac' ? 'INTERAC' : 'Mobile Money'}}
          ({{selectedDepositIssue.status | uppercase}})
        </span>
      </p>
      <p class="mtx__note">
        Signalements envoyés depuis l'application quand un client ne retrouve pas un paiement Interac
        ou un dépôt Mobile Money. Les pièces jointes arrivent sur <strong>info&#64;earlytransfert.com</strong>.
      </p>

      <div class="mtx__grid">
        <ag-grid-angular
          [enableCellTextSelection]="true"
          [ensureDomOrder]="true"
          style="width: 100%; height: 700px;"
          class="ag-theme-balham custom-class"
          [defaultColDef]="defaultColDef"
          [columnDefs]="depositIssuesColumnDefs"
          [rowData]="depositIssuesVisibleRows"
          [animateRows]="true"
          [pagination]="true"
          [rowSelection]="'single'"
          [isExternalFilterPresent]="isExternalFilterPresent"
          [doesExternalFilterPass]="doesExternalFilterPass"
          (rowClicked)="onDepositIssueRowClicked($event)"
          (rowDoubleClicked)="onDepositIssueRowDoubleClicked($event)"
          (gridReady)="onDepositIssuesGridReady($event)">
        </ag-grid-angular>
      </div>
    </div>

    <!-- ═══════════ Notifications ═══════════ -->
    <div *ngIf="activeTab === 'notifications'">

      <div class="mtx-panel mtx-panel--narrow">
        <div class="mtx-panel__body">
          <h2 class="mtx-panel__title">Envoyer une notification push</h2>

          <div class="mtx-field">
            <label class="mtx-field__label" for="notifTitle">Titre *</label>
            <input id="notifTitle" type="text" class="mtx-input" [(ngModel)]="notificationForm.title"
                   placeholder="Titre de la notification…">
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="notifBody">Message *</label>
            <textarea id="notifBody" class="mtx-input" rows="3" [(ngModel)]="notificationForm.body"
                      placeholder="Contenu de la notification…"></textarea>
          </div>

          <div class="mtx-field">
            <span class="mtx-field__label">Destinataires</span>
            <div class="mtx-radios">
              <label class="mtx-radio">
                <input type="radio" id="targetAll" name="targetType"
                       [checked]="notificationTargetType === 'all'"
                       (change)="notificationTargetType = 'all'; notificationForm.targetEmail = ''">
                <span>Tous les utilisateurs</span>
              </label>
              <label class="mtx-radio">
                <input type="radio" id="targetSpecific" name="targetType"
                       [checked]="notificationTargetType === 'specific'"
                       (change)="notificationTargetType = 'specific'">
                <span>Utilisateurs choisis</span>
              </label>
            </div>

            <div *ngIf="notificationTargetType === 'specific'" class="mtx-field__nested">
              <div *ngIf="selectedTargetEmails.length > 0" class="mtx-chips">
                <span *ngFor="let email of selectedTargetEmails" class="mtx-chip">
                  {{email}}
                  <button type="button" class="mtx-chip__remove" (click)="removeTargetEmail(email)"
                          aria-label="Retirer">&times;</button>
                </span>
              </div>

              <input type="text" class="mtx-input"
                     [(ngModel)]="emailTypeaheadInput"
                     [typeahead]="userEmails"
                     [typeaheadMinLength]="0"
                     [typeaheadOptionsLimit]="10"
                     [typeaheadScrollable]="true"
                     [typeaheadOptionsInScrollableView]="6"
                     (typeaheadOnSelect)="onEmailTypeaheadSelect($event)"
                     placeholder="Rechercher et sélectionner des utilisateurs…">
              <small class="mtx-field__hint">Recherche par email ; cliquez pour ajouter. Sélection multiple possible.</small>
            </div>
          </div>

          <div class="mtx-panel__actions">
            <button class="mtx-btn mtx-btn--primary" (click)="sendNotification()" [disabled]="notificationSending">
              <span *ngIf="!notificationSending">Envoyer la notification</span>
              <span *ngIf="notificationSending">Envoi…</span>
            </button>
          </div>
        </div>
      </div>

      <h2 class="mtx__section-title">Historique des notifications</h2>
      <div class="mtx__grid">
        <ag-grid-angular
          [enableCellTextSelection]="true"
          [ensureDomOrder]="true"
          style="width: 100%; height: 500px;"
          class="ag-theme-balham custom-class"
          [defaultColDef]="defaultColDef"
          [columnDefs]="notificationColumnDefs"
          [rowData]="notificationRowData"
          [animateRows]="true"
          [pagination]="true"
          (gridReady)="onNotificationGridReady($event)">
        </ag-grid-angular>
      </div>
    </div>

    <!-- ═══════════ Factures à l'étranger ═══════════ -->
    <div *ngIf="activeTab === 'foreign_bills'">

      <div class="foreign-bill-intro">
        <div class="foreign-bill-intro__icon">
          <i class="fa fa-file-invoice" aria-hidden="true"></i>
        </div>
        <div class="foreign-bill-intro__body">
          <div class="foreign-bill-intro__title">Paiement de factures pour des clients à l'étranger</div>
          <div class="foreign-bill-intro__subtitle">
            Un client hors du Canada demande à un agent sur place de régler une facture
            (scolarité, abonnement, service). L'agent paie, le client rembourse le montant + la commission.
          </div>
        </div>
      </div>

      <div class="mtx__toolbar">
        <button class="mtx-btn mtx-btn--primary" (click)="openForeignBillDetailModal(templateForeignBillDetail)"
                [disabled]="!selectedForeignBill">
          <i class="fa fa-eye" aria-hidden="true"></i> Voir le détail
        </button>
        <button class="mtx-btn mtx-btn--ghost" (click)="pickUpForeignBill()"
                [disabled]="!selectedForeignBill || selectedForeignBill?.status !== 'pending'">
          <i class="fa fa-hand-paper" aria-hidden="true"></i> Prendre en charge
        </button>
        <button class="mtx-btn mtx-btn--success" (click)="openMarkPaidModal(templateForeignBillPaid)"
                [disabled]="!selectedForeignBill || (selectedForeignBill?.status !== 'pending' && selectedForeignBill?.status !== 'in_progress')">
          <i class="fa fa-check-circle" aria-hidden="true"></i> Marquer payée
        </button>
        <button class="mtx-btn mtx-btn--ghost" (click)="markForeignBillReimbursed()"
                [disabled]="!selectedForeignBill || selectedForeignBill?.status !== 'paid'">
          <i class="fa fa-coins" aria-hidden="true"></i> Marquer remboursée
        </button>
        <button class="mtx-btn mtx-btn--danger" (click)="openRejectForeignBillModal(templateForeignBillReject)"
                [disabled]="!selectedForeignBill || selectedForeignBill?.status === 'rejected' || selectedForeignBill?.status === 'reimbursed'">
          <i class="fa fa-ban" aria-hidden="true"></i> Rejeter
        </button>
      </div>
      <p class="mtx__hint">
        <span *ngIf="!selectedForeignBill">Cliquez une ligne pour sélectionner une demande</span>
        <span *ngIf="selectedForeignBill">
          Sélection : <strong>{{selectedForeignBill.userEmail}}</strong> —
          {{formatForeignBillType(selectedForeignBill.billType)}} ·
          {{selectedForeignBill.amount | number:'1.2-2'}} CAD
          ({{selectedForeignBill.status | uppercase}})
        </span>
      </p>

      <div class="mtx__grid">
        <ag-grid-angular
          [enableCellTextSelection]="true"
          [ensureDomOrder]="true"
          style="width: 100%; height: 700px;"
          class="ag-theme-balham custom-class"
          [defaultColDef]="defaultColDef"
          [columnDefs]="foreignBillsColumnDefs"
          [rowData]="foreignBillsRowData"
          [animateRows]="true"
          [pagination]="true"
          [rowSelection]="'single'"
          [isExternalFilterPresent]="isExternalFilterPresent"
          [doesExternalFilterPass]="doesExternalFilterPass"
          (rowClicked)="onForeignBillRowClicked($event)"
          (rowDoubleClicked)="onForeignBillRowDoubleClicked($event)"
          (gridReady)="onForeignBillsGridReady($event)">
        </ag-grid-angular>
      </div>
    </div>

    <!-- ═══════════ Frais ═══════════ -->
    <div *ngIf="activeTab === 'fees'">
      <div class="mtx-panel mtx-panel--narrow">
        <div class="mtx-panel__body">
          <h2 class="mtx-panel__title">Frais de la plateforme (%)</h2>
          <p class="mtx-panel__lead">
            Pourcentage prélevé sur chaque transaction. Le client est débité du montant + le pourcentage ;
            le bénéficiaire reçoit le montant de base, la plateforme gagne la différence.
          </p>

          <div class="mtx-field">
            <label class="mtx-field__label" for="feePost">Frais sur les posts (propriétaire du post) %</label>
            <input id="feePost" type="number" min="0" step="0.01" class="mtx-input"
                   [(ngModel)]="feesForm.postFeePercent" placeholder="0">
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="feeOffer">Frais sur les offres (celui qui fait l'offre) %</label>
            <input id="feeOffer" type="number" min="0" step="0.01" class="mtx-input"
                   [(ngModel)]="feesForm.offerFeePercent" placeholder="0">
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="feeDirect">Frais sur le transfert direct %</label>
            <input id="feeDirect" type="number" min="0" step="0.01" class="mtx-input"
                   [(ngModel)]="feesForm.directTransferFeePercent" placeholder="0">
          </div>

          <div class="mtx-panel__actions">
            <button class="mtx-btn mtx-btn--primary" (click)="saveFees()">Enregistrer les frais</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══════════ Taux de change ═══════════ -->
    <div *ngIf="activeTab === 'rates'">
      <div class="mtx-panel mtx-panel--narrow">
        <div class="mtx-panel__body">
          <h2 class="mtx-panel__title">Taux de change CAD ↔ XAF</h2>
          <p class="mtx-panel__lead">
            Taux appliqué aux transferts directs, dans l'application mobile et sur le site.
            En mode automatique, l'application suit le taux du marché ; en mode manuel, ce sont
            les valeurs saisies ici qui font foi et le taux du marché est ignoré.
            Le marketplace n'est pas concerné : chaque usager y fixe son propre taux dans son post
            — il a ses propres bornes ci-dessous.
          </p>

          <div class="mtx-field">
            <label class="mtx-field__label" for="rateMode">Mode</label>
            <select id="rateMode" class="mtx-input" [(ngModel)]="ratesForm.mode">
              <option value="auto">Automatique — suivre le taux du marché</option>
              <option value="manual">Manuel — appliquer mes taux</option>
            </select>
            <div class="mtx-field__hint" *ngIf="ratesForm.mode === 'auto'">
              Les champs ci-dessous sont conservés mais ne sont pas appliqués tant que le mode
              automatique est actif.
            </div>
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="rateCadXaf">Canada → Cameroun : 1 CAD = ? XAF</label>
            <input id="rateCadXaf" type="number" min="0" step="0.01" class="mtx-input"
                   [(ngModel)]="ratesForm.cadToXaf"
                   [disabled]="ratesForm.mode === 'auto'" placeholder="0">
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="rateXafCad">Cameroun → Canada : 1 XAF = ? CAD</label>
            <input id="rateXafCad" type="number" min="0" step="0.000001" class="mtx-input"
                   [(ngModel)]="ratesForm.xafToCad"
                   [disabled]="ratesForm.mode === 'auto'" placeholder="0">
            <div class="mtx-field__hint">
              Les deux sens sont indépendants : vous pouvez appliquer une marge différente
              à l'aller et au retour.
            </div>
          </div>

          <!-- Ce que le client verra, tel quel, dans l'application. -->
          <div class="mtx-field" *ngIf="ratesForm.mode === 'manual'">
            <label class="mtx-field__label">Aperçu client</label>
            <div class="mtx-code">1 CAD = {{ratesForm.cadToXaf}} XAF</div>
            <div class="mtx-code">1 XAF = {{ratesForm.xafToCad}} CAD</div>
          </div>

          <div class="mtx-field__hint" *ngIf="ratesUpdatedAt">
            Dernière modification : {{ratesUpdatedAt | date:'dd/MM/yyyy HH:mm'}}
            <span *ngIf="ratesUpdatedBy"> par {{ratesUpdatedBy}}</span>
          </div>

          <div class="mtx-panel__actions">
            <button class="mtx-btn mtx-btn--primary" (click)="saveRates()">Enregistrer les taux</button>
          </div>
        </div>
      </div>

      <!-- Bornes du taux que l'usager saisit lui-même sur le marketplace -->
      <div class="mtx-panel mtx-panel--narrow">
        <div class="mtx-panel__body">
          <h2 class="mtx-panel__title">Bornes du taux marketplace</h2>
          <p class="mtx-panel__lead">
            Encadre le taux que l'usager saisit lui-même quand il publie ou modifie une annonce.
            Ce n'est pas un taux appliqué par la plateforme : c'est la fourchette dans laquelle
            sa saisie doit tomber. Un taux hors bornes est refusé à la publication, et une annonce
            qui passerait outre est retirée du marketplace par le serveur.
          </p>

          <div class="mtx-field__hint">
            L'application enregistre toujours le taux d'une annonce dans le même sens —
            <strong>le nombre de XAF pour 1 CAD</strong> — quel que soit le sens de l'annonce
            (Canada → Cameroun ou Cameroun → Canada). Les deux bornes ci-dessous s'expriment donc
            dans cette unité et couvrent les deux sens. Laisser <strong>0</strong> désactive la
            borne concernée.
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="rateBoundMin">Taux minimum autorisé (XAF pour 1 CAD)</label>
            <input id="rateBoundMin" type="number" min="0" step="1" class="mtx-input"
                   [(ngModel)]="rateLimitsForm.min" placeholder="0">
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="rateBoundMax">Taux maximum autorisé (XAF pour 1 CAD)</label>
            <input id="rateBoundMax" type="number" min="0" step="1" class="mtx-input"
                   [(ngModel)]="rateLimitsForm.max" placeholder="0">
            <div class="mtx-field__hint">
              Le minimum doit être inférieur au maximum. Un minimum supérieur ou égal au maximum
              ne laisserait aucun taux valide et bloquerait toute publication d'annonce.
            </div>
          </div>

          <!-- Ce que l'usager pourra saisir, tel quel. -->
          <div class="mtx-field" *ngIf="rateLimitsForm.min > 0 || rateLimitsForm.max > 0">
            <label class="mtx-field__label">Fourchette appliquée</label>
            <div class="mtx-code" *ngIf="rateLimitsForm.min > 0 && rateLimitsForm.max > 0">
              1 CAD = de {{rateLimitsForm.min}} à {{rateLimitsForm.max}} XAF
            </div>
            <div class="mtx-code" *ngIf="rateLimitsForm.min > 0 && !rateLimitsForm.max">
              1 CAD = au moins {{rateLimitsForm.min}} XAF (pas de maximum)
            </div>
            <div class="mtx-code" *ngIf="!rateLimitsForm.min && rateLimitsForm.max > 0">
              1 CAD = au plus {{rateLimitsForm.max}} XAF (pas de minimum)
            </div>
          </div>

          <div class="mtx-field__hint" *ngIf="rateLimitsUpdatedAt">
            Dernière modification : {{rateLimitsUpdatedAt | date:'dd/MM/yyyy HH:mm'}}
            <span *ngIf="rateLimitsUpdatedBy"> par {{rateLimitsUpdatedBy}}</span>
          </div>

          <div class="mtx-panel__actions">
            <button class="mtx-btn mtx-btn--primary" (click)="saveRateLimits()">Enregistrer les bornes</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ═══════════ Paramètres ═══════════ -->
    <!-- ══ PAYS & CORRIDORS ══════════════════════════════════════════════ -->
    <div *ngIf="activeTab === 'pays'">

      <!-- Pays — l'interrupteur le plus grossier : un pays fermé disparaît des
           sélecteurs de l'application, quels que soient ses corridors. -->
      <div class="mtx-panel mtx-panel--narrow">
        <div class="mtx-panel__body">
          <h2 class="mtx-panel__title">Pays disponibles</h2>
          <p class="mtx-panel__lead">
            Ouvre ou ferme un pays, et chaque canal séparément. Un pays fermé n'apparaît plus
            dans les sélecteurs de l'application ; le serveur refuse aussi les opérations qui
            le concernent, donc une application modifiée ne passe pas outre.
          </p>

          <div class="mtx-callout mtx-callout--cta" *ngIf="anyCountryClosed">
            <div class="mtx-callout__title">⚠️ Au moins un pays est fermé</div>
            <div class="mtx-callout__hint">
              Un pays sans entrée ici est <strong>indisponible</strong>, contrairement aux autres
              interrupteurs du tableau de bord : une configuration absente ferme, elle n'ouvre pas.
              Seuls le Canada et le Cameroun restent ouverts par défaut, parce qu'ils existaient
              avant ce document et que des clients y ont déjà de l'argent.
            </div>
          </div>

          <div *ngFor="let c of countryCatalogue" class="mtx-field">
            <h3 class="mtx-panel__title">{{c.label}} <small>({{c.currency}})</small></h3>

            <div class="mtx-field">
              <span class="mtx-field__label">Pays ouvert</span>
              <label class="mtx-switch">
                <input type="checkbox" [id]="'country-' + c.iso"
                       [disabled]="c.hub"
                       [(ngModel)]="countryRow(c.iso).enabled">
                <span class="mtx-switch__track"><span class="mtx-switch__knob"></span></span>
                <span class="mtx-switch__text">
                  {{countryRow(c.iso).enabled ? 'Oui — disponible' : 'Non — indisponible'}}
                </span>
              </label>
              <div class="mtx-field__hint" *ngIf="c.hub">
                Le Cameroun est sur une jambe de chaque corridor : le fermer fermerait toute la
                plateforme d'un coup. Pour cela, utilisez le mode maintenance dans Paramètres.
              </div>
            </div>

            <div class="mtx-field">
              <span class="mtx-field__label">Envoi direct</span>
              <label class="mtx-switch">
                <input type="checkbox" [id]="'country-direct-' + c.iso"
                       [(ngModel)]="countryRow(c.iso).directEnabled">
                <span class="mtx-switch__track"><span class="mtx-switch__knob"></span></span>
                <span class="mtx-switch__text">
                  {{countryRow(c.iso).directEnabled ? 'Oui — autorisé' : 'Non — suspendu'}}
                </span>
              </label>
            </div>

            <div class="mtx-field">
              <span class="mtx-field__label">Marketplace</span>
              <label class="mtx-switch">
                <input type="checkbox" [id]="'country-market-' + c.iso"
                       [(ngModel)]="countryRow(c.iso).marketplaceEnabled">
                <span class="mtx-switch__track"><span class="mtx-switch__knob"></span></span>
                <span class="mtx-switch__text">
                  {{countryRow(c.iso).marketplaceEnabled ? 'Oui — autorisé' : 'Non — suspendu'}}
                </span>
              </label>
            </div>

            <div class="mtx-field">
              <label class="mtx-field__label" [for]="'country-fr-' + c.iso">Message (français)</label>
              <textarea [id]="'country-fr-' + c.iso" rows="2" class="mtx-input"
                        [(ngModel)]="countryRow(c.iso).messageFr"
                        placeholder="Ex : Les envois depuis ce pays ouvriront prochainement."></textarea>
            </div>

            <div class="mtx-field">
              <label class="mtx-field__label" [for]="'country-en-' + c.iso">Message (anglais)</label>
              <textarea [id]="'country-en-' + c.iso" rows="2" class="mtx-input"
                        [(ngModel)]="countryRow(c.iso).messageEn"
                        placeholder="Ex: Transfers from this country are coming soon."></textarea>
            </div>
          </div>

          <div class="mtx-field__hint" *ngIf="countriesUpdatedAt">
            Dernière modification : {{countriesUpdatedAt | date:'dd/MM/yyyy HH:mm'}}
            <span *ngIf="countriesUpdatedBy"> par {{countriesUpdatedBy}}</span>
          </div>

          <div class="mtx-panel__actions">
            <button class="mtx-btn mtx-btn--primary" (click)="saveCountries()">
              Enregistrer les pays
            </button>
          </div>
        </div>
      </div>

      <!-- Coordonnées de virement — sans elles, un client peut ouvrir un
           portefeuille sans jamais pouvoir l'alimenter. -->
      <div class="mtx-panel mtx-panel--narrow">
        <div class="mtx-panel__body">
          <h2 class="mtx-panel__title">Coordonnées de virement bancaire</h2>
          <p class="mtx-panel__lead">
            Pour les pays alimentés par virement (le Canada utilise Interac, le Cameroun Mobile
            Money). Le client vire sur ce compte, déclare son virement dans l'application, et
            vous créditez son portefeuille depuis l'onglet des dépôts.
          </p>

          <div class="mtx-callout">
            <div class="mtx-callout__title">🔒 Ces informations sont publiques</div>
            <div class="mtx-callout__hint">
              Le document de configuration est lisible sans authentification, pour que l'écran de
              démarrage puisse le lire avant connexion. C'est sans conséquence pour un compte de
              réception d'entreprise — mais n'y mettez jamais de clé ni de référence interne.
            </div>
          </div>

          <div *ngFor="let c of bankRailCountries" class="mtx-field">
            <h3 class="mtx-panel__title">{{c.label}} <small>({{c.currency}})</small></h3>

            <div class="mtx-field">
              <span class="mtx-field__label">Rail actif</span>
              <label class="mtx-switch">
                <input type="checkbox" [id]="'rail-' + c.iso" [(ngModel)]="railRow(c.iso).enabled">
                <span class="mtx-switch__track"><span class="mtx-switch__knob"></span></span>
                <span class="mtx-switch__text">
                  {{railRow(c.iso).enabled ? 'Oui — virements acceptés' : 'Non — pas encore disponible'}}
                </span>
              </label>
            </div>

            <div class="mtx-field">
              <label class="mtx-field__label" [for]="'rail-bank-' + c.iso">Banque</label>
              <input [id]="'rail-bank-' + c.iso" type="text" class="mtx-input"
                     [(ngModel)]="railRow(c.iso).bankName" placeholder="Ex : Barclays">
            </div>

            <div class="mtx-field">
              <label class="mtx-field__label" [for]="'rail-holder-' + c.iso">Titulaire du compte</label>
              <input [id]="'rail-holder-' + c.iso" type="text" class="mtx-input"
                     [(ngModel)]="railRow(c.iso).accountName" placeholder="Ex : Early Transfert Ltd">
            </div>

            <div class="mtx-field">
              <span class="mtx-field__label">Coordonnées</span>
              <div class="mtx-field__hint">
                Libellé et valeur, dans l'ordre d'affichage. Chaque pays a les siennes — IBAN et
                BIC en France, sort code et numéro de compte au Royaume-Uni — d'où une liste libre
                plutôt que des colonnes fixes.
              </div>
              <div *ngFor="let f of railRow(c.iso).fields; let i = index" class="mtx-field">
                <input type="text" class="mtx-input" [(ngModel)]="f.label"
                       [name]="'rail-' + c.iso + '-label-' + i" placeholder="Libellé (ex : IBAN)">
                <input type="text" class="mtx-input" [(ngModel)]="f.value"
                       [name]="'rail-' + c.iso + '-value-' + i" placeholder="Valeur">
                <button class="mtx-btn" (click)="removeRailField(c.iso, i)">Supprimer</button>
              </div>
              <button class="mtx-btn" (click)="addRailField(c.iso)">Ajouter une coordonnée</button>
            </div>

            <div class="mtx-field">
              <label class="mtx-field__label" [for]="'rail-ref-' + c.iso">Référence à porter sur le virement</label>
              <input [id]="'rail-ref-' + c.iso" type="text" class="mtx-input"
                     [(ngModel)]="railRow(c.iso).referenceHint"
                     placeholder="Ex : votre adresse email">
              <div class="mtx-field__hint">
                Obligatoire pour activer le rail. Sans référence, un virement entrant ne peut pas
                être rattaché à un client, et le crédit manuel devient une devinette.
              </div>
            </div>

            <div class="mtx-field">
              <label class="mtx-field__label" [for]="'rail-instr-fr-' + c.iso">Instructions (français)</label>
              <textarea [id]="'rail-instr-fr-' + c.iso" rows="3" class="mtx-input"
                        [(ngModel)]="railRow(c.iso).instructionsFr"></textarea>
            </div>

            <div class="mtx-field">
              <label class="mtx-field__label" [for]="'rail-instr-en-' + c.iso">Instructions (anglais)</label>
              <textarea [id]="'rail-instr-en-' + c.iso" rows="3" class="mtx-input"
                        [(ngModel)]="railRow(c.iso).instructionsEn"></textarea>
            </div>

            <div class="mtx-field">
              <label class="mtx-field__label" [for]="'rail-note-fr-' + c.iso">Délai annoncé (français)</label>
              <input [id]="'rail-note-fr-' + c.iso" type="text" class="mtx-input"
                     [(ngModel)]="railRow(c.iso).processingNoteFr"
                     placeholder="Ex : Crédit sous 24 h ouvrées après réception.">
            </div>

            <div class="mtx-field">
              <label class="mtx-field__label" [for]="'rail-note-en-' + c.iso">Délai annoncé (anglais)</label>
              <input [id]="'rail-note-en-' + c.iso" type="text" class="mtx-input"
                     [(ngModel)]="railRow(c.iso).processingNoteEn"
                     placeholder="Ex: Credited within 1 business day of arrival.">
            </div>
          </div>

          <div class="mtx-field__hint" *ngIf="depositRailsUpdatedAt">
            Dernière modification : {{depositRailsUpdatedAt | date:'dd/MM/yyyy HH:mm'}}
            <span *ngIf="depositRailsUpdatedBy"> par {{depositRailsUpdatedBy}}</span>
          </div>

          <div class="mtx-panel__actions">
            <button class="mtx-btn mtx-btn--primary" (click)="saveDepositRails()">
              Enregistrer les coordonnées
            </button>
          </div>
        </div>
      </div>

      <!-- Directions — plus fin que l'interrupteur pays : suspend un seul sens. -->
      <div class="mtx-panel mtx-panel--narrow">
        <div class="mtx-panel__body">
          <h2 class="mtx-panel__title">Directions du corridor</h2>
          <p class="mtx-panel__lead">
            Suspend un seul sens des transferts, sans fermer le pays ni la plateforme. Utile en
            cas de rupture de liquidité ou d'incident chez un partenaire de paiement.
          </p>

          <div class="mtx-callout mtx-callout--cta" *ngIf="anyCorridorClosed">
            <div class="mtx-callout__title">⚠️ Une direction est suspendue</div>
            <div class="mtx-callout__hint">
              Attention : cela ne concerne que les <strong>transferts directs</strong>.
              Les annonces et les offres traversent le même corridor et restent ouvertes —
              de l'argent peut donc continuer à circuler dans le sens fermé via le marketplace.
              Pour l'arrêter aussi, coupez le canal Marketplace du pays ci-dessus, ou le
              marketplace entier depuis Paramètres.
            </div>
          </div>

          <div *ngFor="let d of corridorDirections" class="mtx-field">
            <h3 class="mtx-panel__title">{{d.label}}</h3>

            <div class="mtx-field">
              <span class="mtx-field__label">Suspendre cette direction</span>
              <label class="mtx-switch">
                <input type="checkbox" [id]="d.key + 'Switch'"
                       [(ngModel)]="corridorSide(d.key).disabled">
                <span class="mtx-switch__track"><span class="mtx-switch__knob"></span></span>
                <span class="mtx-switch__text">
                  {{corridorSide(d.key).disabled ? 'Oui — suspendue' : 'Non — ouverte'}}
                </span>
              </label>
            </div>

            <div class="mtx-field">
              <label class="mtx-field__label" [for]="d.key + 'Fr'">Message (français)</label>
              <textarea [id]="d.key + 'Fr'" rows="2" class="mtx-input"
                        [(ngModel)]="corridorSide(d.key).messageFr"
                        placeholder="Ex : Les envois dans ce sens sont suspendus le temps de rétablir la liquidité."></textarea>
            </div>

            <div class="mtx-field">
              <label class="mtx-field__label" [for]="d.key + 'En'">Message (anglais)</label>
              <textarea [id]="d.key + 'En'" rows="2" class="mtx-input"
                        [(ngModel)]="corridorSide(d.key).messageEn"
                        placeholder="Ex: Transfers in this direction are paused while we restore liquidity."></textarea>
            </div>
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="corridorExempt">Comptes exemptés</label>
            <input id="corridorExempt" type="text" class="mtx-input"
                   [(ngModel)]="corridorsForm.exemptEmails"
                   placeholder="admin@exemple.com, test@exemple.com">
            <div class="mtx-field__hint">
              Emails séparés par des virgules. Ces comptes continuent de transférer dans les
              directions suspendues — pratique pour vérifier avant de rouvrir.
            </div>
          </div>

          <div class="mtx-field__hint" *ngIf="corridorsUpdatedAt">
            Dernière modification : {{corridorsUpdatedAt | date:'dd/MM/yyyy HH:mm'}}
            <span *ngIf="corridorsUpdatedBy"> par {{corridorsUpdatedBy}}</span>
          </div>

          <div class="mtx-panel__actions">
            <button class="mtx-btn mtx-btn--primary" (click)="saveCorridors()">
              Enregistrer les directions
            </button>
          </div>
        </div>
      </div>
    </div>

    <div *ngIf="activeTab === 'settings'">

      <!-- Mode maintenance — le réglage le plus lourd de conséquences, en premier -->
      <div class="mtx-panel mtx-panel--narrow">
        <div class="mtx-panel__body">
          <h2 class="mtx-panel__title">Mode maintenance</h2>
          <p class="mtx-panel__lead">
            Rend la plateforme inaccessible à tous : l'application mobile et le site public
            affichent votre message au lieu de leur contenu habituel, et les transferts sont
            refusés côté serveur. Ce tableau de bord reste toujours accessible.
          </p>

          <div class="mtx-callout mtx-callout--cta" *ngIf="maintenanceForm.isActive">
            <div class="mtx-callout__title">⚠️ Plateforme actuellement fermée</div>
            <div class="mtx-callout__hint">
              Les usagers ne peuvent ni ouvrir l'application ni envoyer d'argent.
              Désactivez l'interrupteur puis enregistrez pour rouvrir.
            </div>
          </div>

          <div class="mtx-field">
            <span class="mtx-field__label">Fermer la plateforme</span>
            <label class="mtx-switch">
              <input type="checkbox" id="maintenanceActiveSwitch" [(ngModel)]="maintenanceForm.isActive">
              <span class="mtx-switch__track"><span class="mtx-switch__knob"></span></span>
              <span class="mtx-switch__text">
                {{maintenanceForm.isActive ? 'Oui — service suspendu' : 'Non — service ouvert'}}
              </span>
            </label>
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="maintTitleFr">Titre (français)</label>
            <textarea id="maintTitleFr" rows="2" class="mtx-input"
                      [(ngModel)]="maintenanceForm.titleFr"
                      placeholder="Ex : Maintenance en cours"></textarea>
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="maintMessageFr">Message (français)</label>
            <textarea id="maintMessageFr" rows="3" class="mtx-input"
                      [(ngModel)]="maintenanceForm.messageFr"
                      placeholder="Ex : Nous améliorons nos services. L'application sera de nouveau disponible sous peu."></textarea>
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="maintTitleEn">Titre (anglais)</label>
            <textarea id="maintTitleEn" rows="2" class="mtx-input"
                      [(ngModel)]="maintenanceForm.titleEn"
                      placeholder="Ex: Under maintenance"></textarea>
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="maintMessageEn">Message (anglais)</label>
            <textarea id="maintMessageEn" rows="3" class="mtx-input"
                      [(ngModel)]="maintenanceForm.messageEn"
                      placeholder="Ex: We are improving our services. The app will be available again shortly."></textarea>
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="maintExempt">Comptes exemptés</label>
            <input id="maintExempt" type="text" class="mtx-input"
                   [(ngModel)]="maintenanceForm.exemptEmails"
                   placeholder="admin@exemple.com, test@exemple.com">
            <div class="mtx-field__hint">
              Emails séparés par des virgules. Ces comptes continuent d'utiliser l'application
              normalement pendant la fermeture — pratique pour vérifier que tout remarche avant
              de rouvrir au public.
            </div>
          </div>

          <div class="mtx-field__hint" *ngIf="maintenanceUpdatedAt">
            Dernière modification : {{maintenanceUpdatedAt | date:'dd/MM/yyyy HH:mm'}}
            <span *ngIf="maintenanceUpdatedBy"> par {{maintenanceUpdatedBy}}</span>
          </div>

          <div class="mtx-panel__actions">
            <button class="mtx-btn mtx-btn--primary" (click)="saveMaintenanceConfig()">
              {{maintenanceForm.isActive ? 'Fermer la plateforme' : 'Enregistrer'}}
            </button>
          </div>
        </div>
      </div>

      <!-- Société — entité exploitante et inscription FINTRAC affichées dans le dossier investisseur -->
      <div class="mtx-panel mtx-panel--narrow">
        <div class="mtx-panel__body">
          <h2 class="mtx-panel__title">Société et inscription FINTRAC</h2>
          <p class="mtx-panel__lead">
            L'entité qui exploite EarlyTransfert et son numéro d'inscription FINTRAC, tels qu'ils
            apparaissent dans le dossier investisseur (business case et business plan). Le dossier
            en ligne affiche la nouvelle valeur d'ici une minute, sans redéploiement.
          </p>

          <div class="mtx-field">
            <label class="mtx-field__label" for="companyLegalName">Nom légal de la société</label>
            <input id="companyLegalName" type="text" class="mtx-input"
                   [(ngModel)]="companyForm.legalName"
                   placeholder="Ex : Vofack Consultation Inc.">
            <div class="mtx-field__hint">
              Écrivez-le exactement comme au registre des entreprises et au registre FINTRAC.
            </div>
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="companyFintrac">Numéro d'inscription FINTRAC</label>
            <input id="companyFintrac" type="text" class="mtx-input"
                   [(ngModel)]="companyForm.fintracNumber"
                   placeholder="Ex : C10001562">
            <div class="mtx-field__hint">
              Une lettre suivie de 8 chiffres. Vérifiable dans le registre public des entreprises de
              services monétaires de FINTRAC — l'équipe du destinataire pourra le contrôler.
            </div>
          </div>

          <div class="mtx-field__hint" *ngIf="companyUpdatedAt">
            Dernière modification : {{companyUpdatedAt | date:'dd/MM/yyyy HH:mm'}}
            <span *ngIf="companyUpdatedBy"> par {{companyUpdatedBy}}</span>
          </div>

          <div class="mtx-panel__actions">
            <button class="mtx-btn mtx-btn--primary" (click)="saveCompanyConfig()">
              Enregistrer la société
            </button>
          </div>
        </div>
      </div>

      <!-- Dossier investisseur — un mot de passe par document protégé (business case, business plan) -->
      <div class="mtx-panel mtx-panel--narrow" *ngFor="let panel of dossierPasswordPanels">
        <div class="mtx-panel__body">
          <h2 class="mtx-panel__title">{{panel.title}}</h2>
          <p class="mtx-panel__lead">
            {{panel.lead}} Le changer ferme immédiatement les sessions ouvertes sur ce document :
            pensez à communiquer le nouveau mot de passe aux destinataires.
          </p>

          <ul class="mtx-list">
            <li *ngFor="let link of panel.links">
              {{link.label}} :
              <a [href]="link.url" target="_blank" rel="noopener">{{link.url}}</a>
            </li>
          </ul>

          <div class="mtx-field">
            <label class="mtx-field__label" [attr.for]="'dossierPassword-' + panel.target">Nouveau mot de passe</label>
            <input [id]="'dossierPassword-' + panel.target" class="mtx-input" autocomplete="new-password"
                   [type]="dossierPasswordVisible[panel.target] ? 'text' : 'password'"
                   [(ngModel)]="dossierPasswordForm[panel.target].password"
                   placeholder="Au moins 10 caractères">
            <div class="mtx-field__hint">
              Il sera tapé à la main depuis un courriel : préférez trois mots séparés par des tirets,
              sans majuscule ni caractère ambigu (ex : {{panel.example}}).
            </div>
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" [attr.for]="'dossierPasswordConfirm-' + panel.target">Confirmer le mot de passe</label>
            <input [id]="'dossierPasswordConfirm-' + panel.target" class="mtx-input" autocomplete="new-password"
                   [type]="dossierPasswordVisible[panel.target] ? 'text' : 'password'"
                   [(ngModel)]="dossierPasswordForm[panel.target].confirm">
          </div>

          <div class="mtx-field">
            <label class="mtx-switch">
              <input type="checkbox" [id]="'dossierPasswordShow-' + panel.target" [(ngModel)]="dossierPasswordVisible[panel.target]">
              <span class="mtx-switch__track"><span class="mtx-switch__knob"></span></span>
              <span class="mtx-switch__text">Afficher la saisie</span>
            </label>
          </div>

          <div class="mtx-field__hint" *ngIf="dossierPasswordInfo[panel.target] as info">
            <ng-container *ngIf="info.source === 'dashboard' && info.updatedAt">
              Dernière modification : {{info.updatedAt | date:'dd/MM/yyyy HH:mm'}}
              <span *ngIf="info.updatedBy"> par {{info.updatedBy}}</span>
            </ng-container>
            <ng-container *ngIf="info.source === 'secret'">
              Aucun mot de passe défini ici pour l'instant : c'est celui du déploiement initial qui s'applique.
            </ng-container>
            <ng-container *ngIf="info.source === 'none'">
              Aucun mot de passe défini : ce document reste inaccessible tant qu'il n'a pas été choisi.
            </ng-container>
          </div>

          <div class="mtx-panel__actions">
            <button class="mtx-btn mtx-btn--primary" [disabled]="dossierPasswordSaving[panel.target]" (click)="saveDossierPassword(panel.target)">
              {{dossierPasswordSaving[panel.target] ? 'Enregistrement…' : 'Changer le mot de passe'}}
            </button>
          </div>
        </div>
      </div>

      <!-- Marketplace — ferme les annonces et les offres, sans toucher aux transferts -->
      <div class="mtx-panel mtx-panel--narrow">
        <div class="mtx-panel__body">
          <h2 class="mtx-panel__title">Marketplace</h2>
          <p class="mtx-panel__lead">
            Ferme le marketplace (annonces et offres entre clients) sans toucher aux transferts
            directs. Les clients concernés voient un écran d'indisponibilité portant le message
            ci-dessous.
          </p>

          <div class="mtx-callout mtx-callout--cta" *ngIf="marketplaceClosed">
            <div class="mtx-callout__title">⚠️ Le marketplace est fermé</div>
            <div class="mtx-callout__hint">
              Les transferts directs restent ouverts. Attention : un client dont l'offre vient
              d'être acceptée ne verra plus sa transaction ni les coordonnées de dépôt tant que
              le marketplace est fermé — ajoutez-le aux comptes exemptés si besoin.
            </div>
          </div>

          <div class="mtx-field">
            <span class="mtx-field__label">Fermer le marketplace</span>
            <label class="mtx-switch">
              <input type="checkbox" id="marketplaceSwitch" [(ngModel)]="marketplaceConfigForm.disabled">
              <span class="mtx-switch__track"><span class="mtx-switch__knob"></span></span>
              <span class="mtx-switch__text">
                {{marketplaceConfigForm.disabled ? 'Oui — fermé pour tous' : 'Non — ouvert'}}
              </span>
            </label>
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="mpTitleFr">Titre (français)</label>
            <input id="mpTitleFr" type="text" class="mtx-input"
                   [(ngModel)]="marketplaceConfigForm.titleFr"
                   placeholder="Ex : Marketplace temporairement fermé">
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="mpTitleEn">Titre (anglais)</label>
            <input id="mpTitleEn" type="text" class="mtx-input"
                   [(ngModel)]="marketplaceConfigForm.titleEn"
                   placeholder="Ex: Marketplace temporarily closed">
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="mpMsgFr">Message (français)</label>
            <textarea id="mpMsgFr" rows="3" class="mtx-input"
                      [(ngModel)]="marketplaceConfigForm.messageFr"
                      placeholder="Ex : Le marketplace est en pause le temps d'une vérification. Les transferts directs fonctionnent normalement."></textarea>
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="mpMsgEn">Message (anglais)</label>
            <textarea id="mpMsgEn" rows="3" class="mtx-input"
                      [(ngModel)]="marketplaceConfigForm.messageEn"
                      placeholder="Ex: The marketplace is paused while we run a check. Direct transfers are working normally."></textarea>
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="mpExempt">Comptes exemptés</label>
            <input id="mpExempt" type="text" class="mtx-input"
                   [(ngModel)]="marketplaceConfigForm.exemptEmails"
                   placeholder="admin@exemple.com, test@exemple.com">
            <div class="mtx-field__hint">
              Emails séparés par des virgules. Ces comptes gardent l'accès au marketplace
              pendant qu'il est fermé — pratique pour vérifier avant de rouvrir, ou pour
              débloquer un client dont l'offre vient d'être acceptée.
            </div>
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="mpBlocked">Comptes bloqués</label>
            <input id="mpBlocked" type="text" class="mtx-input"
                   [(ngModel)]="marketplaceConfigForm.blockedEmails"
                   placeholder="fraude@exemple.com">
            <div class="mtx-field__hint">
              Emails séparés par des virgules. Ces comptes perdent l'accès au marketplace
              même lorsqu'il est ouvert. Un email présent dans les deux listes est bloqué.
            </div>
          </div>

          <div class="mtx-field__hint" *ngIf="marketplaceConfigUpdatedAt">
            Dernière modification : {{marketplaceConfigUpdatedAt | date:'dd/MM/yyyy HH:mm'}}
            <span *ngIf="marketplaceConfigUpdatedBy"> par {{marketplaceConfigUpdatedBy}}</span>
          </div>

          <div class="mtx-panel__actions">
            <button class="mtx-btn mtx-btn--primary" (click)="saveMarketplaceConfig()">
              {{marketplaceConfigForm.disabled ? 'Fermer le marketplace' : 'Enregistrer'}}
            </button>
          </div>
        </div>
      </div>

      <!-- Plafonds par transaction -->
      <div class="mtx-panel mtx-panel--narrow">
        <div class="mtx-panel__body">
          <h2 class="mtx-panel__title">Plafonds par transaction</h2>
          <p class="mtx-panel__lead">
            Deux seuils par flux. Au-delà du <strong>seuil de revue</strong>, l'usager doit
            fournir un dossier (pièces, motif, profession, source des fonds) et la transaction
            attend votre approbation. Au-delà du <strong>plafond</strong>, elle est refusée.
            Les seuils sont par devise : 500 CAD et 500 XAF ne représentent pas la même somme.
          </p>
          <div class="mtx-field__hint">
            Laisser <strong>0</strong> désactive le seuil concerné. Le seuil de revue doit être
            inférieur au plafond.
          </div>

          <h3 class="mtx-panel__title">Transfert direct</h3>

          <div class="mtx-field">
            <label class="mtx-field__label" for="limDirectCadReview">Seuil de revue (CAD)</label>
            <input id="limDirectCadReview" type="number" min="0" step="1" class="mtx-input"
                   [(ngModel)]="limitsForm.direct.CAD.reviewAbove" placeholder="0">
          </div>
          <div class="mtx-field">
            <label class="mtx-field__label" for="limDirectCadBlock">Plafond (CAD)</label>
            <input id="limDirectCadBlock" type="number" min="0" step="1" class="mtx-input"
                   [(ngModel)]="limitsForm.direct.CAD.blockAbove" placeholder="0">
          </div>
          <div class="mtx-field">
            <label class="mtx-field__label" for="limDirectXafReview">Seuil de revue (XAF)</label>
            <input id="limDirectXafReview" type="number" min="0" step="1" class="mtx-input"
                   [(ngModel)]="limitsForm.direct.XAF.reviewAbove" placeholder="0">
          </div>
          <div class="mtx-field">
            <label class="mtx-field__label" for="limDirectXafBlock">Plafond (XAF)</label>
            <input id="limDirectXafBlock" type="number" min="0" step="1" class="mtx-input"
                   [(ngModel)]="limitsForm.direct.XAF.blockAbove" placeholder="0">
          </div>

          <h3 class="mtx-panel__title">Marketplace (annonces et offres)</h3>

          <div class="mtx-field">
            <label class="mtx-field__label" for="limMktCadReview">Seuil de revue (CAD)</label>
            <input id="limMktCadReview" type="number" min="0" step="1" class="mtx-input"
                   [(ngModel)]="limitsForm.marketplace.CAD.reviewAbove" placeholder="0">
          </div>
          <div class="mtx-field">
            <label class="mtx-field__label" for="limMktCadBlock">Plafond (CAD)</label>
            <input id="limMktCadBlock" type="number" min="0" step="1" class="mtx-input"
                   [(ngModel)]="limitsForm.marketplace.CAD.blockAbove" placeholder="0">
          </div>
          <div class="mtx-field">
            <label class="mtx-field__label" for="limMktXafReview">Seuil de revue (XAF)</label>
            <input id="limMktXafReview" type="number" min="0" step="1" class="mtx-input"
                   [(ngModel)]="limitsForm.marketplace.XAF.reviewAbove" placeholder="0">
          </div>
          <div class="mtx-field">
            <label class="mtx-field__label" for="limMktXafBlock">Plafond (XAF)</label>
            <input id="limMktXafBlock" type="number" min="0" step="1" class="mtx-input"
                   [(ngModel)]="limitsForm.marketplace.XAF.blockAbove" placeholder="0">
          </div>

          <div class="mtx-field__hint" *ngIf="limitsUpdatedAt">
            Dernière modification : {{limitsUpdatedAt | date:'dd/MM/yyyy HH:mm'}}
            <span *ngIf="limitsUpdatedBy"> par {{limitsUpdatedBy}}</span>
          </div>

          <div class="mtx-panel__actions">
            <button class="mtx-btn mtx-btn--primary" (click)="saveLimits()">Enregistrer les plafonds</button>
          </div>
        </div>
      </div>

      <!-- Domaine de l'alias Interac (Canada) -->
      <div class="mtx-panel mtx-panel--narrow">
        <div class="mtx-panel__body">
          <h2 class="mtx-panel__title">Domaine de l'adresse Interac (Canada)</h2>
          <p class="mtx-panel__lead">
            Domaine affiché aux clients canadiens dans leur adresse de réception dédiée
            (<code class="mtx-code">alias&#64;{{interacForm.aliasDomain || 'domaine'}}</code>).
            Lu par l'application au moment du dépôt Interac.
          </p>

          <div class="mtx-field">
            <label class="mtx-field__label" for="interacDomain">Domaine</label>
            <input id="interacDomain" type="text" class="mtx-input"
                   [(ngModel)]="interacForm.aliasDomain" placeholder="earlytransfert.com">
          </div>

          <div class="mtx-panel__actions">
            <button class="mtx-btn mtx-btn--primary" (click)="saveInteracConfig()">Enregistrer le domaine</button>
          </div>
        </div>
      </div>

      <!-- Numéros Mobile Money (Cameroun) -->
      <div class="mtx-panel mtx-panel--narrow">
        <div class="mtx-panel__body">
          <h2 class="mtx-panel__title">Numéros Mobile Money (Cameroun)</h2>
          <p class="mtx-panel__lead">
            Numéros de réception MTN et Orange affichés aux clients camerounais sur la page
            de dépôt Mobile Money.
          </p>

          <div class="mtx-field">
            <label class="mtx-field__label" for="momoMtn">Numéro MTN MoMo</label>
            <input id="momoMtn" type="text" class="mtx-input"
                   [(ngModel)]="momoForm.mtnNumber" placeholder="Ex : 6XXXXXXXX">
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="momoOrange">Numéro Orange Money</label>
            <input id="momoOrange" type="text" class="mtx-input"
                   [(ngModel)]="momoForm.orangeNumber" placeholder="Ex : 6XXXXXXXX">
          </div>

          <div class="mtx-panel__actions">
            <button class="mtx-btn mtx-btn--primary" (click)="saveMomoConfig()">Enregistrer les numéros</button>
          </div>
        </div>
      </div>

    </div>

    <!-- ═══════════ Politique de confidentialité ═══════════ -->
    <div *ngIf="activeTab === 'privacy'">

      <!-- En-tête : ce que le client voit en haut de l'écran -->
      <div class="mtx-panel mtx-panel--narrow">
        <div class="mtx-panel__body">
          <h2 class="mtx-panel__title">En-tête</h2>
          <p class="mtx-panel__lead">
            Bandeau affiché en haut de la politique dans l'application, avant les sections.
          </p>

          <div class="mtx-field">
            <label class="mtx-field__label" for="ppEffFr">Date d'effet (français)</label>
            <input id="ppEffFr" type="text" class="mtx-input"
                   [(ngModel)]="privacyForm.effectiveDateFr" placeholder="Ex : Date d'effet : 14 juin 2026">
          </div>
          <div class="mtx-field">
            <label class="mtx-field__label" for="ppEffEn">Date d'effet (anglais)</label>
            <input id="ppEffEn" type="text" class="mtx-input"
                   [(ngModel)]="privacyForm.effectiveDateEn" placeholder="Ex : Effective date: June 14, 2026">
          </div>
          <div class="mtx-field">
            <label class="mtx-field__label" for="ppIntroFr">Introduction (français)</label>
            <textarea id="ppIntroFr" rows="4" class="mtx-input" [(ngModel)]="privacyForm.introFr"></textarea>
          </div>
          <div class="mtx-field">
            <label class="mtx-field__label" for="ppIntroEn">Introduction (anglais)</label>
            <textarea id="ppIntroEn" rows="4" class="mtx-input" [(ngModel)]="privacyForm.introEn"></textarea>
          </div>
        </div>
      </div>

      <!-- Sections — l'ordre ici est l'ordre d'affichage dans l'application -->
      <div class="mtx-panel mtx-panel--narrow">
        <div class="mtx-panel__body">
          <h2 class="mtx-panel__title">Sections</h2>
          <p class="mtx-panel__lead">
            Chaque section est une carte dans l'application. L'ordre ci-dessous est l'ordre d'affichage.
            Les retours à la ligne sont conservés : utilisez « • » en début de ligne pour une liste à puces.
          </p>

          <div class="mtx-empty" *ngIf="privacySections.length === 0">
            Aucune section. Utilisez « Pré-remplir » pour partir de la politique embarquée dans l'application.
          </div>

          <div class="mtx-secitem" *ngFor="let sec of privacySections; let i = index; trackBy: trackBySectionIndex">
            <div class="mtx-secitem__head">
              <span class="mtx-secitem__rank">Section {{i + 1}}</span>
              <span class="mtx-secitem__tools">
                <button class="mtx-btn mtx-btn--ghost mtx-btn--sm" (click)="movePrivacySection(i, -1)"
                        [disabled]="i === 0" title="Monter">↑</button>
                <button class="mtx-btn mtx-btn--ghost mtx-btn--sm" (click)="movePrivacySection(i, 1)"
                        [disabled]="i === privacySections.length - 1" title="Descendre">↓</button>
                <button class="mtx-btn mtx-btn--danger mtx-btn--sm" (click)="removePrivacySection(i)"
                        title="Supprimer">Supprimer</button>
              </span>
            </div>

            <div class="mtx-field">
              <label class="mtx-field__label" for="ppIcon{{i}}">Icône</label>
              <select id="ppIcon{{i}}" class="mtx-input" [(ngModel)]="sec.icon">
                <option *ngFor="let opt of privacyIconOptions" [value]="opt.key">{{opt.label}}</option>
              </select>
            </div>

            <div class="mtx-field">
              <label class="mtx-field__label" for="ppTitleFr{{i}}">Titre (français)</label>
              <input id="ppTitleFr{{i}}" type="text" class="mtx-input" [(ngModel)]="sec.titleFr">
            </div>
            <div class="mtx-field">
              <label class="mtx-field__label" for="ppTitleEn{{i}}">Titre (anglais)</label>
              <input id="ppTitleEn{{i}}" type="text" class="mtx-input" [(ngModel)]="sec.titleEn">
            </div>
            <div class="mtx-field">
              <label class="mtx-field__label" for="ppBodyFr{{i}}">Corps (français)</label>
              <textarea id="ppBodyFr{{i}}" rows="6" class="mtx-input" [(ngModel)]="sec.bodyFr"></textarea>
            </div>
            <div class="mtx-field">
              <label class="mtx-field__label" for="ppBodyEn{{i}}">Corps (anglais)</label>
              <textarea id="ppBodyEn{{i}}" rows="6" class="mtx-input" [(ngModel)]="sec.bodyEn"></textarea>
            </div>
          </div>

          <div class="mtx-panel__actions">
            <button class="mtx-btn mtx-btn--ghost" (click)="addPrivacySection()">+ Ajouter une section</button>
            <button class="mtx-btn mtx-btn--ghost" (click)="seedPrivacyDefaults()">Pré-remplir</button>
          </div>
        </div>
      </div>

      <!-- Pied de page : bloc contact et date de mise à jour -->
      <div class="mtx-panel mtx-panel--narrow">
        <div class="mtx-panel__body">
          <h2 class="mtx-panel__title">Contact et pied de page</h2>

          <div class="mtx-field">
            <label class="mtx-field__label" for="ppContactEmail">Adresse de contact</label>
            <input id="ppContactEmail" type="email" class="mtx-input"
                   [(ngModel)]="privacyForm.contactEmail" placeholder="privacy@earlytransfert.com">
            <span class="mtx-field__hint">Le bouton « Écrire à notre équipe » de l'application ouvre cette adresse.</span>
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="ppContactTitleFr">Titre du bloc contact (français)</label>
            <input id="ppContactTitleFr" type="text" class="mtx-input" [(ngModel)]="privacyForm.contactTitleFr">
          </div>
          <div class="mtx-field">
            <label class="mtx-field__label" for="ppContactTitleEn">Titre du bloc contact (anglais)</label>
            <input id="ppContactTitleEn" type="text" class="mtx-input" [(ngModel)]="privacyForm.contactTitleEn">
          </div>
          <div class="mtx-field">
            <label class="mtx-field__label" for="ppContactBodyFr">Texte du bloc contact (français)</label>
            <textarea id="ppContactBodyFr" rows="3" class="mtx-input" [(ngModel)]="privacyForm.contactBodyFr"></textarea>
          </div>
          <div class="mtx-field">
            <label class="mtx-field__label" for="ppContactBodyEn">Texte du bloc contact (anglais)</label>
            <textarea id="ppContactBodyEn" rows="3" class="mtx-input" [(ngModel)]="privacyForm.contactBodyEn"></textarea>
          </div>

          <div class="mtx-field">
            <label class="mtx-field__label" for="ppUpdFr">Dernière mise à jour (français)</label>
            <input id="ppUpdFr" type="text" class="mtx-input" [(ngModel)]="privacyForm.lastUpdatedFr">
          </div>
          <div class="mtx-field">
            <label class="mtx-field__label" for="ppUpdEn">Dernière mise à jour (anglais)</label>
            <input id="ppUpdEn" type="text" class="mtx-input" [(ngModel)]="privacyForm.lastUpdatedEn">
          </div>

          <div class="mtx-panel__actions">
            <button class="mtx-btn mtx-btn--ghost" (click)="stampPrivacyToday()">Remplir avec la date du jour</button>
          </div>
        </div>
      </div>

      <!-- Publication — la seule chose qui sollicite les clients -->
      <div class="mtx-panel mtx-panel--narrow">
        <div class="mtx-panel__body">
          <h2 class="mtx-panel__title">Publication</h2>
          <p class="mtx-panel__lead">
            Version publiée actuellement : <strong>{{privacyVersion || '—'}}</strong>.
            Enregistrer corrige le texte sans rien demander aux clients ; publier une nouvelle version
            oblige chacun à l'accepter de nouveau à sa prochaine connexion.
          </p>

          <div class="mtx-callout mtx-callout--cta" *ngIf="privacyBumpVersion">
            À l'enregistrement, la version passera à {{(privacyVersion || 0) + 1}} et
            <strong>tous les clients</strong> devront accepter de nouveau la politique.
          </div>

          <div class="mtx-field">
            <span class="mtx-field__label">Publier comme nouvelle version</span>
            <label class="mtx-switch">
              <input type="checkbox" id="privacyBumpSwitch" [(ngModel)]="privacyBumpVersion">
              <span class="mtx-switch__track"><span class="mtx-switch__knob"></span></span>
              <span class="mtx-switch__text">
                {{privacyBumpVersion ? 'Oui — exiger une nouvelle acceptation' : 'Non — simple correction du texte'}}
              </span>
            </label>
          </div>

          <div class="mtx-field__hint" *ngIf="privacyUpdatedAt">
            Dernière modification : {{privacyUpdatedAt | date:'dd/MM/yyyy HH:mm'}}
            <span *ngIf="privacyUpdatedBy"> par {{privacyUpdatedBy}}</span>
          </div>
          <div class="mtx-field__hint" *ngIf="privacyVersionPublishedAt">
            Version {{privacyVersion}} publiée le {{privacyVersionPublishedAt | date:'dd/MM/yyyy HH:mm'}}
          </div>

          <div class="mtx-panel__actions">
            <button class="mtx-btn mtx-btn--primary" (click)="savePrivacyPolicy()">{{privacySaveLabel}}</button>
          </div>
        </div>
      </div>

    </div>

  </main>
</div>
<!-- / .mtx -->



<!-- ════════════════════════════════════════════════════════════
     MODALES — rendues hors du DOM du composant par BsModalService.
     Leur portée de style vient de la classe `mtx-modal` posée sur
     `.modal-dialog` via ModalOptions.class, côté TypeScript.
     ════════════════════════════════════════════════════════════ -->

<!-- ── AJOUTER UN BÉNÉFICIAIRE ── -->
<ng-template #template1>

  <div class="modal-header mtx-modal__head mtx-modal__head--brand">
    <h4 class="modal-title mtx-modal__title" i18n>Edit</h4>
    <button type="button" class="close mtx-modal__close" aria-label="Close" (click)="modalRef.hide()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body">
    <form>
      <div class="mtx-field__label" i18n>Identification</div>
      <div class="row mtx-field">
        <div class="col">
          <input type="text" class="mtx-input" #Inputprenom placeholder="First name">
          <div *ngIf="enablePrenom" class="mtx-field__error" i18n>First name required please !</div>
        </div>
        <div class="col">
          <input type="text" class="mtx-input" #Inputnom placeholder="Name">
          <div *ngIf="enableNom" class="mtx-field__error" i18n>Name required please !</div>
        </div>
      </div>

      <div class="row mtx-field">
        <div class="col">
          <input type="text" class="mtx-input" #InputCountry placeholder="Country">
          <div *ngIf="enableCountry" class="mtx-field__error" i18n>Country required please !</div>
        </div>
        <div class="col">
          <input type="text" class="mtx-input" #InputTown placeholder="City">
          <div *ngIf="enableTown" class="mtx-field__error" i18n>City required please !</div>
        </div>
        <div class="col">
          <input type="text" class="mtx-input" #InputProvince placeholder="Province">
        </div>
      </div>

      <div class="mtx-field">
        <div class="mtx-field__label" i18n>Phone</div>
        <form #f="ngForm" [formGroup]="phoneForm">
          <!-- <div class="wrapper">
            <ngx-intl-tel-input [cssClass]="'custom'" [preferredCountries]="preferredCountries"
              [enableAutoCountrySelect]="true" [enablePlaceholder]="true" [searchCountryFlag]="true"
              [searchCountryField]="[SearchCountryField.Iso2, SearchCountryField.Name]" [selectFirstCountry]="false"
              [selectedCountryISO]="CountryISO.Canada" [maxLength]="15"
              [phoneValidation]="true" [separateDialCode]="true" (ngModelChange)="onChange($event)"
              [numberFormat]="PhoneNumberFormat.National" name="phone" formControlName="phone">
            </ngx-intl-tel-input>
          </div> -->
          <!-- <div class="wrapper">
            <button (click)="f.reset()">Reset</button>
          </div> -->
        </form>
        <div *ngIf="enableTelephone" class="mtx-field__error" i18n>Phone-- required please !</div>
      </div>

      <div class="mtx-field">
        <label class="mtx-field__label" for="exampleInputEmail1" i18n>Email address</label>
        <input type="email" class="mtx-input" id="exampleInputEmail1" #Inputemail aria-describedby="emailHelp" placeholder="Enter email" required>
        <div *ngIf="enableEmail" class="mtx-field__error" i18n>Email required please !</div>
      </div>
    </form>
  </div>

  <div class="modal-footer">
    <button type="submit" class="mtx-btn mtx-btn--primary" (click)="ajouter(Inputnom,Inputprenom,InputCountry,InputProvince,InputTown,Inputemail)" i18n>Create</button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="modalRef.hide()" i18n>Close</button>
  </div>

</ng-template>
<!-- / AJOUTER UN BÉNÉFICIAIRE -->

<!-- ── CHANGER LE SOLDE ── -->
<ng-template #templateSolde>

  <div class="modal-header mtx-modal__head mtx-modal__head--brand">
    <h4 class="modal-title mtx-modal__title" i18n>Edit</h4>
    <button type="button" class="close mtx-modal__close" aria-label="Close" (click)="modalRef.hide()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body">
    <form>
      <div class="mtx-field">
        <label class="mtx-field__label" for="exampleInputsolde" i18n>New Balance</label>
        <input type="number" step="0.001" min="0" inputmode="decimal" class="mtx-input"
               id="exampleInputsolde" #Inputsolde placeholder="Nouveau solde" required>
        <div *ngIf="enableAmount" class="mtx-field__error" i18n>Balance required please !</div>
      </div>
    </form>
  </div>

  <div class="modal-footer">
    <button type="submit" class="mtx-btn mtx-btn--primary" (click)="modifier(Inputsolde)" i18n>Change</button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="modalRef.hide()" i18n>Close</button>
  </div>

</ng-template>
<!-- / CHANGER LE SOLDE -->

<!-- ── DÉTAIL KYC ── -->
<!-- CONFORMITÉ : dossier -->
<ng-template #templateComplianceDetail>

  <div class="modal-header mtx-modal__head mtx-modal__head--brand">
    <h4 class="modal-title mtx-modal__title">Dossier de conformité</h4>
    <button type="button" class="close mtx-modal__close" aria-label="Close" (click)="modalRef.hide()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body" *ngIf="selectedCompliance">
    <div class="row">
      <div class="col-md-6">
        <p><strong>Email :</strong> {{selectedCompliance.userEmail}}</p>
        <p><strong>Montant :</strong> {{selectedCompliance.amount}} {{selectedCompliance.currency}}</p>
        <p><strong>Type :</strong>
          {{ {direct: 'Transfert direct', post: 'Annonce', offer: 'Offre'}[selectedCompliance.kind] }}
        </p>
        <p><strong>Transaction retenue :</strong> {{compliancePayloadSummary(selectedCompliance)}}</p>
      </div>
      <div class="col-md-6">
        <p><strong>Statut :</strong>
          <span class="mtx-status" [ngClass]="{
            'mtx-status--ok': selectedCompliance.status === 'approved',
            'mtx-status--ko': selectedCompliance.status === 'rejected' || selectedCompliance.status === 'failed',
            'mtx-status--wait': selectedCompliance.status === 'pending'
          }">
            {{selectedCompliance.status | uppercase}}
          </span>
        </p>
        <p><strong>Soumis :</strong> {{selectedCompliance.submittedAt}}</p>
        <p *ngIf="selectedCompliance.decidedAt"><strong>Décidé :</strong> {{selectedCompliance.decidedAt}}</p>
        <p *ngIf="selectedCompliance.decidedBy"><strong>Par :</strong> {{selectedCompliance.decidedBy}}</p>
        <p *ngIf="selectedCompliance.rejectionReason">
          <strong>{{selectedCompliance.status === 'failed' ? 'Erreur de règlement' : 'Motif'}} :</strong>
          {{selectedCompliance.rejectionReason}}
        </p>
        <p *ngIf="selectedCompliance.remediation"><strong>Démarche :</strong> {{selectedCompliance.remediation}}</p>
      </div>
    </div>

    <hr class="mtx-modal__rule">

    <div class="row">
      <div class="col-md-4">
        <h5 class="mtx-modal__subtitle">Motif de l'envoi</h5>
        <p>{{selectedCompliance.reason || '—'}}</p>
      </div>
      <div class="col-md-4">
        <h5 class="mtx-modal__subtitle">Profession</h5>
        <p>{{selectedCompliance.profession || '—'}}</p>
      </div>
      <div class="col-md-4">
        <h5 class="mtx-modal__subtitle">Source des fonds</h5>
        <p>{{selectedCompliance.sourceOfFunds || '—'}}</p>
      </div>
    </div>

    <hr class="mtx-modal__rule">

    <h5 class="mtx-modal__subtitle">Justificatifs</h5>
    <div class="row">
      <div class="col-md-4" *ngFor="let url of selectedCompliance.documentUrls; let i = index">
        <img [src]="url" class="kyc-photo" alt="Justificatif">
        <button type="button" class="kyc-download-btn"
          (click)="downloadKycImage(url, 'compliance_' + selectedCompliance.userEmail + '_' + (i + 1) + '.jpg')">
          <i class="fa fa-download" aria-hidden="true"></i> Télécharger
        </button>
      </div>
      <div class="col-md-12" *ngIf="!selectedCompliance.documentUrls?.length">
        <div class="kyc-photo-placeholder">Aucun justificatif</div>
      </div>
    </div>
  </div>

  <div class="modal-footer" *ngIf="selectedCompliance?.status === 'pending'">
    <button type="button" class="mtx-btn mtx-btn--success" (click)="approveCompliance(selectedCompliance)">Approuver</button>
    <button type="button" class="mtx-btn mtx-btn--danger"
            (click)="openComplianceRejectModal(templateComplianceReject, selectedCompliance)">Rejeter</button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="modalRef.hide()">Fermer</button>
  </div>
  <div class="modal-footer" *ngIf="selectedCompliance?.status !== 'pending'">
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="modalRef.hide()">Fermer</button>
  </div>
</ng-template>
<!-- / CONFORMITÉ : dossier -->

<!-- CONFORMITÉ : rejet -->
<ng-template #templateComplianceReject>

  <div class="modal-header mtx-modal__head mtx-modal__head--danger">
    <h4 class="modal-title mtx-modal__title">Rejeter le dossier</h4>
    <button type="button" class="close mtx-modal__close" aria-label="Close" (click)="modalRef.hide()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body">
    <p class="mtx-panel__lead">
      Les deux champs sont envoyés à l'usager : dites-lui pourquoi c'est refusé, et ce qu'il doit
      faire pour y remédier.
    </p>
    <div class="mtx-field">
      <label class="mtx-field__label" for="compRejectReason">Motif du refus</label>
      <textarea id="compRejectReason" class="mtx-input" rows="3"
                [(ngModel)]="complianceRejectReason"
                placeholder="Ex : le justificatif de provenance des fonds est illisible."></textarea>
    </div>
    <div class="mtx-field">
      <label class="mtx-field__label" for="compRemediation">Démarche à suivre</label>
      <textarea id="compRemediation" class="mtx-input" rows="3"
                [(ngModel)]="complianceRemediation"
                placeholder="Ex : renvoyez une photo nette du relevé bancaire des 3 derniers mois."></textarea>
    </div>
  </div>

  <div class="modal-footer">
    <button type="button" class="mtx-btn mtx-btn--danger" (click)="rejectCompliance()">Confirmer le refus</button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="modalRef.hide()">Annuler</button>
  </div>
</ng-template>
<!-- / CONFORMITÉ : rejet -->

<ng-template #templateKycDetail>

  <div class="modal-header mtx-modal__head mtx-modal__head--brand">
    <h4 class="modal-title mtx-modal__title">KYC Verification Details</h4>
    <button type="button" class="close mtx-modal__close" aria-label="Close" (click)="modalRef.hide()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body" *ngIf="selectedKyc">
    <div class="row">
      <div class="col-md-4">
        <h5 class="mtx-modal__subtitle">ID Document — Front (Recto)</h5>
        <img *ngIf="selectedKyc.idPhotoUrl" [src]="selectedKyc.idPhotoUrl" class="kyc-photo" alt="ID Document Front">
        <div *ngIf="!selectedKyc.idPhotoUrl" class="kyc-photo-placeholder">No image available</div>
        <button *ngIf="selectedKyc.idPhotoUrl" type="button" class="kyc-download-btn"
          (click)="downloadKycImage(selectedKyc.idPhotoUrl, 'kyc_' + selectedKyc.userEmail + '_id_front.jpg')">
          <i class="fa fa-download" aria-hidden="true"></i> Download
        </button>
      </div>
      <div class="col-md-4">
        <h5 class="mtx-modal__subtitle">ID Document — Back (Verso)</h5>
        <img *ngIf="selectedKyc.idBackPhotoUrl" [src]="selectedKyc.idBackPhotoUrl" class="kyc-photo" alt="ID Document Back">
        <div *ngIf="!selectedKyc.idBackPhotoUrl" class="kyc-photo-placeholder">No image available</div>
        <button *ngIf="selectedKyc.idBackPhotoUrl" type="button" class="kyc-download-btn"
          (click)="downloadKycImage(selectedKyc.idBackPhotoUrl, 'kyc_' + selectedKyc.userEmail + '_id_back.jpg')">
          <i class="fa fa-download" aria-hidden="true"></i> Download
        </button>
      </div>
      <div class="col-md-4">
        <h5 class="mtx-modal__subtitle">Selfie</h5>
        <img *ngIf="selectedKyc.selfieUrl" [src]="selectedKyc.selfieUrl" class="kyc-photo" alt="Selfie">
        <div *ngIf="!selectedKyc.selfieUrl" class="kyc-photo-placeholder">No image available</div>
        <button *ngIf="selectedKyc.selfieUrl" type="button" class="kyc-download-btn"
          (click)="downloadKycImage(selectedKyc.selfieUrl, 'kyc_' + selectedKyc.userEmail + '_selfie.jpg')">
          <i class="fa fa-download" aria-hidden="true"></i> Download
        </button>
      </div>
    </div>

    <hr class="mtx-modal__rule">

    <div class="row">
      <div class="col-md-6">
        <p><strong>Email:</strong> {{selectedKyc.userEmail}}</p>
        <p><strong>Extracted Name:</strong> {{selectedKyc.extractedName || 'N/A'}}</p>
        <p><strong>Document #:</strong> {{selectedKyc.extractedDocumentNumber || 'N/A'}}</p>
        <p *ngIf="selectedKyc.attemptNumber"><strong>Attempt:</strong> #{{selectedKyc.attemptNumber}} of {{selectedKyc.totalAttempts}}</p>
      </div>
      <div class="col-md-6">
        <p><strong>Status:</strong>
          <span class="mtx-status" [ngClass]="{
            'mtx-status--ok': selectedKyc.status === 'verified',
            'mtx-status--ko': selectedKyc.status === 'rejected',
            'mtx-status--wait': selectedKyc.status === 'pending'
          }">
            {{selectedKyc.status | uppercase}}
          </span>
        </p>
        <p><strong>Submitted:</strong> {{selectedKyc.submittedAt}}</p>
        <p *ngIf="selectedKyc.verifiedAt"><strong>Verified:</strong> {{selectedKyc.verifiedAt}}</p>
        <p *ngIf="selectedKyc.rejectionReason"><strong>Rejection Reason:</strong> {{selectedKyc.rejectionReason}}</p>
      </div>
    </div>
  </div>

  <div class="modal-footer" *ngIf="selectedKyc?.status === 'pending'">
    <button type="button" class="mtx-btn mtx-btn--success" (click)="approveKyc(selectedKyc)">Approve</button>
    <button type="button" class="mtx-btn mtx-btn--danger" (click)="openRejectModal(templateReject, selectedKyc)">Reject</button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="modalRef.hide()">Close</button>
  </div>
  <div class="modal-footer" *ngIf="selectedKyc?.status !== 'pending'">
    <button type="button" class="mtx-btn mtx-btn--warning" (click)="setKycPending(selectedKyc)">Back to Pending</button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="modalRef.hide()">Close</button>
  </div>

</ng-template>
<!-- / DÉTAIL KYC -->

<!-- ── FOREIGN BILL : DÉTAIL ── -->
<ng-template #templateForeignBillDetail>
  <div class="modal-header mtx-modal__head foreign-bill-modal__header">
    <div class="mtx-modal__head-group">
      <div class="foreign-bill-modal__icon">
        <i class="fa fa-file-invoice" aria-hidden="true"></i>
      </div>
      <div>
        <h4 class="modal-title mtx-modal__title">
          Foreign bill payment request
        </h4>
        <small class="mtx-modal__subtle" *ngIf="selectedForeignBill">
          {{selectedForeignBill.userEmail}}
        </small>
      </div>
    </div>
    <button type="button" class="close mtx-modal__close" aria-label="Close" (click)="modalRef.hide()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body" *ngIf="selectedForeignBill">
    <span class="foreign-bill-status" [ngClass]="'foreign-bill-status--' + selectedForeignBill.status">
      {{selectedForeignBill.status | uppercase}}
    </span>

    <div class="foreign-bill-grid">
      <div class="foreign-bill-grid__item">
        <div class="foreign-bill-grid__label">Bill type</div>
        <div class="foreign-bill-grid__value">{{formatForeignBillType(selectedForeignBill.billType)}}</div>
      </div>
      <div class="foreign-bill-grid__item">
        <div class="foreign-bill-grid__label">Provider</div>
        <div class="foreign-bill-grid__value">{{selectedForeignBill.providerName || '-'}}</div>
      </div>
      <div class="foreign-bill-grid__item">
        <div class="foreign-bill-grid__label">Reference</div>
        <div class="foreign-bill-grid__value">{{selectedForeignBill.accountReference || '-'}}</div>
      </div>
      <div class="foreign-bill-grid__item">
        <div class="foreign-bill-grid__label">Website</div>
        <div class="foreign-bill-grid__value">
          <a *ngIf="selectedForeignBill.providerWebsite" [href]="selectedForeignBill.providerWebsite" target="_blank">
            {{selectedForeignBill.providerWebsite}}
          </a>
          <span *ngIf="!selectedForeignBill.providerWebsite">-</span>
        </div>
      </div>
      <div class="foreign-bill-grid__item">
        <div class="foreign-bill-grid__label">Due date</div>
        <div class="foreign-bill-grid__value">{{selectedForeignBill.dueDate || '-'}}</div>
      </div>
      <div class="foreign-bill-grid__item">
        <div class="foreign-bill-grid__label">Reimbursement</div>
        <div class="foreign-bill-grid__value">{{formatReimbursementMethod(selectedForeignBill.reimbursementMethod)}}</div>
      </div>
      <div class="foreign-bill-grid__item">
        <div class="foreign-bill-grid__label">Contact phone</div>
        <div class="foreign-bill-grid__value">{{selectedForeignBill.contactPhone || '-'}}</div>
      </div>
      <div class="foreign-bill-grid__item">
        <div class="foreign-bill-grid__label">Destination</div>
        <div class="foreign-bill-grid__value">{{selectedForeignBill.destinationCountry || 'Canada'}}</div>
      </div>
    </div>

    <div class="foreign-bill-amounts">
      <div class="foreign-bill-amounts__row">
        <span>Bill amount</span>
        <strong>{{selectedForeignBill.amount | number:'1.2-2'}} CAD</strong>
      </div>
      <div class="foreign-bill-amounts__row">
        <span>Commission</span>
        <strong>+ {{selectedForeignBill.commission | number:'1.2-2'}} CAD</strong>
      </div>
      <div class="foreign-bill-amounts__total">
        <span>Estimated reimbursement</span>
        <strong>{{selectedForeignBill.estimatedTotal | number:'1.2-2'}} CAD</strong>
      </div>
    </div>

    <div *ngIf="selectedForeignBill.notes" class="foreign-bill-notes">
      <div class="foreign-bill-grid__label">User notes</div>
      <div class="foreign-bill-notes__body">{{selectedForeignBill.notes}}</div>
    </div>

    <div *ngIf="selectedForeignBill.paymentReference || selectedForeignBill.proofUrl || selectedForeignBill.adminNote"
         class="foreign-bill-notes foreign-bill-notes--admin">
      <div class="foreign-bill-grid__label">Admin receipt</div>
      <div *ngIf="selectedForeignBill.paymentReference"><strong>Reference:</strong> {{selectedForeignBill.paymentReference}}</div>
      <div *ngIf="selectedForeignBill.proofUrl">
        <strong>Proof:</strong>
        <a [href]="selectedForeignBill.proofUrl" target="_blank">{{selectedForeignBill.proofUrl}}</a>
      </div>
      <div *ngIf="selectedForeignBill.adminNote"><strong>Note:</strong> {{selectedForeignBill.adminNote}}</div>
    </div>

    <div *ngIf="selectedForeignBill.rejectionReason" class="foreign-bill-notes foreign-bill-notes--rejected">
      <div class="foreign-bill-grid__label">Rejection reason</div>
      <div class="foreign-bill-notes__body">{{selectedForeignBill.rejectionReason}}</div>
    </div>

    <div class="foreign-bill-notes foreign-bill-user-message">
      <div class="foreign-bill-grid__label">Message to user (visible in their app — both languages required)</div>

      <div class="mtx-field">
        <label class="mtx-field__label mtx-field__label--warn">English</label>
        <textarea class="mtx-input" rows="3"
                  [(ngModel)]="foreignBillUserMessageEnDraft"
                  placeholder="E.g. Please send a clearer photo of the invoice, or confirm the account number..."></textarea>
      </div>

      <div class="mtx-field">
        <label class="mtx-field__label mtx-field__label--warn">Français</label>
        <textarea class="mtx-input" rows="3"
                  [(ngModel)]="foreignBillUserMessageFrDraft"
                  placeholder="Ex. Merci d'envoyer une photo plus nette de la facture, ou de confirmer le numéro de compte..."></textarea>
      </div>

      <small class="mtx-field__hint" *ngIf="selectedForeignBill.userMessageEn || selectedForeignBill.userMessageFr || selectedForeignBill.userMessage">
        Currently visible to user (shown in their app's language).
      </small>
      <small class="mtx-field__hint" *ngIf="!selectedForeignBill.userMessageEn && !selectedForeignBill.userMessageFr && !selectedForeignBill.userMessage">
        No message currently visible to user.
      </small>

      <div class="mtx__toolbar mtx__toolbar--start">
        <button type="button" class="mtx-btn mtx-btn--primary mtx-btn--sm"
                (click)="saveForeignBillUserMessage()"
                [disabled]="foreignBillUserMessageSaving || !hasForeignBillUserMessageChanges()">
          {{ ((foreignBillUserMessageEnDraft || '').trim() || (foreignBillUserMessageFrDraft || '').trim()) ? 'Send to user' : 'Clear message' }}
        </button>
        <button type="button" class="mtx-btn mtx-btn--ghost mtx-btn--sm"
                (click)="clearForeignBillUserMessageDraft()"
                [disabled]="foreignBillUserMessageSaving || (!foreignBillUserMessageEnDraft && !foreignBillUserMessageFrDraft)">
          Reset draft
        </button>
      </div>
    </div>
  </div>

  <div class="modal-footer">
    <button type="button" class="mtx-btn mtx-btn--info" (click)="pickUpForeignBill()"
            [disabled]="!selectedForeignBill || selectedForeignBill?.status !== 'pending'">
      Pick up
    </button>
    <button type="button" class="mtx-btn mtx-btn--success" (click)="openMarkPaidModal(templateForeignBillPaid)"
            [disabled]="!selectedForeignBill || (selectedForeignBill?.status !== 'pending' && selectedForeignBill?.status !== 'in_progress')">
      Mark as paid
    </button>
    <button type="button" class="mtx-btn mtx-btn--warning" (click)="markForeignBillReimbursed()"
            [disabled]="!selectedForeignBill || selectedForeignBill?.status !== 'paid'">
      Mark reimbursed
    </button>
    <button type="button" class="mtx-btn mtx-btn--danger" (click)="openRejectForeignBillModal(templateForeignBillReject)"
            [disabled]="!selectedForeignBill || selectedForeignBill?.status === 'rejected' || selectedForeignBill?.status === 'reimbursed'">
      Reject
    </button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="modalRef.hide()">Close</button>
  </div>
</ng-template>
<!-- / FOREIGN BILL : DÉTAIL -->

<!-- ── FOREIGN BILL : MARQUER PAYÉ ── -->
<ng-template #templateForeignBillPaid>
  <div class="modal-header mtx-modal__head mtx-modal__head--violet">
    <h4 class="modal-title mtx-modal__title">Mark bill as paid</h4>
    <button type="button" class="close mtx-modal__close" aria-label="Close" (click)="modalRef.hide()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body">
    <div class="mtx-field">
      <label class="mtx-field__label" for="fbPaidRef">Payment reference (optional)</label>
      <input id="fbPaidRef" type="text" class="mtx-input" [(ngModel)]="foreignBillPaidForm.reference"
             placeholder="Transaction ID, receipt number...">
    </div>
    <div class="mtx-field">
      <label class="mtx-field__label" for="fbPaidProof">Proof URL (optional)</label>
      <input id="fbPaidProof" type="url" class="mtx-input" [(ngModel)]="foreignBillPaidForm.proofUrl"
             placeholder="https://...">
      <small class="mtx-field__hint">A link to the receipt, screenshot, or confirmation.</small>
    </div>
    <div class="mtx-field">
      <label class="mtx-field__label" for="fbPaidNote">Admin note (optional)</label>
      <textarea id="fbPaidNote" class="mtx-input" rows="3" [(ngModel)]="foreignBillPaidForm.note"
                placeholder="Anything else to record for this payment..."></textarea>
    </div>
  </div>

  <div class="modal-footer">
    <button type="button" class="mtx-btn mtx-btn--success" (click)="confirmMarkPaid()">
      Confirm payment
    </button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="modalRef.hide()">Cancel</button>
  </div>
</ng-template>
<!-- / FOREIGN BILL : MARQUER PAYÉ -->

<!-- ── FOREIGN BILL : REJET ── -->
<ng-template #templateForeignBillReject>
  <div class="modal-header mtx-modal__head mtx-modal__head--danger">
    <h4 class="modal-title mtx-modal__title">Reject request</h4>
    <button type="button" class="close mtx-modal__close" aria-label="Close" (click)="modalRef.hide()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body">
    <p class="mtx-modal__lead">
      The user will see this reason in their app. Be specific so they can resubmit
      a corrected request if needed.
    </p>
    <div class="mtx-field">
      <label class="mtx-field__label" for="fbRejectReason">Reason *</label>
      <textarea id="fbRejectReason" class="mtx-input" rows="4" [(ngModel)]="foreignBillRejectionReason"
                placeholder="e.g. The provider is outside Canada, reference number incorrect..."></textarea>
    </div>
  </div>

  <div class="modal-footer">
    <button type="button" class="mtx-btn mtx-btn--danger" (click)="confirmRejectForeignBill()">
      Reject request
    </button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="modalRef.hide()">Cancel</button>
  </div>
</ng-template>
<!-- / FOREIGN BILL : REJET -->

<!-- ── ÉDITION DU WALLET ── -->
<ng-template #templateEditWallet>

  <div class="modal-header mtx-modal__head mtx-modal__head--brand">
    <h4 class="modal-title mtx-modal__title">Edit Wallet Balance</h4>
    <button type="button" class="close mtx-modal__close" aria-label="Close" (click)="modalRef.hide()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body" *ngIf="selectedWallet">
    <p><strong>User:</strong> {{selectedWallet.usersEmail}}</p>
    <p><strong>Currency:</strong> {{selectedWallet.currency}} ({{selectedWallet.countryCode}})</p>
    <p *ngIf="selectedWallet.interacPhone"><strong>Interac Phone:</strong> {{selectedWallet.interacPhone}}</p>
    <p *ngIf="selectedWallet.mobileMoney"><strong>Mobile Money:</strong> {{selectedWallet.mobileMoney}}</p>

    <div *ngIf="selectedWallet.countryCode === 'CA'" class="mtx-callout">
      <p class="mtx-callout__label"><strong>Interac Alias (unique receiving address):</strong></p>
      <ng-container *ngIf="selectedWallet.interacAlias; else noAlias">
        <code class="mtx-code">{{selectedWallet.interacAlias}}&#64;{{interacAliasDomain}}</code>
        <button type="button" class="mtx-btn mtx-btn--ghost mtx-btn--sm"
                (click)="copyInteracAlias()">Copy</button>
      </ng-container>
      <ng-template #noAlias>
        <span class="mtx-muted">— not assigned —</span>
        <button type="button" class="mtx-btn mtx-btn--success mtx-btn--sm"
                [disabled]="aliasGenerating"
                (click)="generateInteracAlias(selectedWallet.id)">
          {{ aliasGenerating ? 'Generating…' : 'Generate Interac Alias' }}
        </button>
      </ng-template>
    </div>

    <div *ngIf="selectedWallet.countryCode === 'CM'" class="mtx-callout">
      <p class="mtx-callout__label"><strong>MoMo Code (unique customer reference):</strong></p>
      <ng-container *ngIf="selectedWallet.momoCode; else noMomoCode">
        <code class="mtx-code mtx-code--lg">{{selectedWallet.momoCode}}</code>
        <button type="button" class="mtx-btn mtx-btn--ghost mtx-btn--sm"
                (click)="copyMomoCode()">Copy</button>
        <p class="mtx-callout__hint">
          The customer enters this code in the "reason" / "motif" field of their Mobile Money transfer.
        </p>
      </ng-container>
      <ng-template #noMomoCode>
        <span class="mtx-muted">— not assigned —</span>
        <button type="button" class="mtx-btn mtx-btn--success mtx-btn--sm"
                [disabled]="momoCodeGenerating"
                (click)="generateMomoCode(selectedWallet.id)">
          {{ momoCodeGenerating ? 'Generating…' : 'Generate MoMo Code' }}
        </button>
      </ng-template>
    </div>

    <div class="mtx-field">
      <label class="mtx-field__label" for="walletAmount">New Balance ({{selectedWallet.currency}})</label>
      <input type="number" class="mtx-input" id="walletAmount" min="0" step="0.01"
             [(ngModel)]="walletNewAmount" placeholder="Enter new balance">
    </div>
  </div>

  <div class="modal-footer">
    <button type="button" class="mtx-btn mtx-btn--success" (click)="saveWalletAmount()">Save</button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="modalRef.hide()">Cancel</button>
  </div>

</ng-template>
<!-- / ÉDITION DU WALLET -->

<!-- ── AJOUT / ÉDITION D'UN MESSAGE ── -->
<ng-template #templateAddMessage>

  <div class="modal-header mtx-modal__head mtx-modal__head--brand">
    <h4 class="modal-title mtx-modal__title">
      {{isEditingMessage ? 'Edit Message' : 'Add New Message'}}
    </h4>
    <button type="button" class="close mtx-modal__close" aria-label="Close" (click)="modalRef.hide()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body">
    <div class="mtx-field">
      <label class="mtx-field__label" for="msgEn">Message in English *</label>
      <textarea id="msgEn" class="mtx-input" rows="3" [(ngModel)]="messageForm.messageEn"
                placeholder="Enter the message in English..."></textarea>
    </div>
    <div class="mtx-field">
      <label class="mtx-field__label" for="msgFr">Message en Français *</label>
      <textarea id="msgFr" class="mtx-input" rows="3" [(ngModel)]="messageForm.messageFr"
                placeholder="Entrez le message en français..."></textarea>
    </div>
    <div class="row">
      <div class="col-md-6">
        <div class="mtx-field">
          <label class="mtx-field__label" for="msgType">Type</label>
          <select id="msgType" class="mtx-input" [(ngModel)]="messageForm.type">
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
          </select>
        </div>
      </div>
      <div class="col-md-6">
        <div class="mtx-field">
          <span class="mtx-field__label">Active</span>
          <label class="mtx-switch">
            <input type="checkbox" id="isActiveSwitch" [(ngModel)]="messageForm.isActive">
            <span class="mtx-switch__track"><span class="mtx-switch__knob"></span></span>
            <span class="mtx-switch__text">
              {{messageForm.isActive ? 'Yes (visible to users)' : 'No (hidden)'}}
            </span>
          </label>
        </div>
      </div>
    </div>
    <div class="mtx-field">
      <label class="mtx-field__label" for="msgTarget">Target User (optional)</label>
      <input id="msgTarget" type="email" class="mtx-input" [(ngModel)]="messageForm.targetUserEmail"
             placeholder="Leave empty to show to all users">
      <small class="mtx-field__hint">Enter a user email to send this message only to that specific user.</small>
    </div>
  </div>

  <div class="modal-footer">
    <button type="button" class="mtx-btn mtx-btn--success" (click)="saveMessage()">
      {{isEditingMessage ? 'Update' : 'Save'}}
    </button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="modalRef.hide()">Cancel</button>
  </div>

</ng-template>
<!-- / AJOUT / ÉDITION D'UN MESSAGE -->

<!-- ── REJET KYC ── -->
<ng-template #templateReject>

  <div class="modal-header mtx-modal__head mtx-modal__head--danger">
    <h4 class="modal-title mtx-modal__title">Reject KYC Verification</h4>
    <button type="button" class="close mtx-modal__close" aria-label="Close" (click)="modalRef.hide()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body">
    <p><strong>User:</strong> {{selectedKyc?.userEmail}}</p>
    <div class="mtx-field">
      <label class="mtx-field__label" for="rejectionReason">Reason for rejection:</label>
      <textarea class="mtx-input" id="rejectionReason" rows="3"
                [(ngModel)]="rejectionReason"
                placeholder="Enter the reason for rejecting this KYC verification..."></textarea>
    </div>
  </div>

  <div class="modal-footer">
    <button type="button" class="mtx-btn mtx-btn--danger" (click)="rejectKyc()">Confirm Rejection</button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="modalRef.hide()">Cancel</button>
  </div>

</ng-template>
<!-- / REJET KYC -->

<!-- ── CORPS DE L'EMAIL INTERAC ── -->
<ng-template #templateInteracBody>

  <div class="modal-header mtx-modal__head mtx-modal__head--info">
    <h4 class="modal-title mtx-modal__title">Interac Email Details</h4>
    <button type="button" class="close mtx-modal__close" aria-label="Close" (click)="modalRef.hide()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body" *ngIf="selectedInteracEmail">
    <div class="row">
      <div class="col-md-6">
        <p><strong>From:</strong> {{selectedInteracEmail.senderName}} &lt;{{selectedInteracEmail.from}}&gt;</p>
        <p><strong>Subject:</strong> {{selectedInteracEmail.subject}}</p>
      </div>
      <div class="col-md-6">
        <p><strong>Date:</strong> {{selectedInteracEmail.date | date:'medium'}}</p>
        <p><strong>Amount:</strong> {{selectedInteracEmail.amount || 'N/A'}}</p>
        <p><strong>Status:</strong>
          <span class="mtx-status" [ngClass]="{
            'mtx-status--ok': selectedInteracEmail.status === 'processed',
            'mtx-status--ko': selectedInteracEmail.status === 'ignored',
            'mtx-status--wait': selectedInteracEmail.status === 'new'
          }">
            {{selectedInteracEmail.status | uppercase}}
          </span>
        </p>
      </div>
    </div>

    <hr class="mtx-modal__rule">

    <div class="mtx-pre">{{selectedInteracEmail.body}}</div>
  </div>

  <div class="modal-footer">
    <button type="button" class="mtx-btn mtx-btn--success" (click)="markInteracProcessed(); modalRef.hide();"
            *ngIf="selectedInteracEmail?.status !== 'processed'">
      Mark Processed
    </button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="modalRef.hide()">Close</button>
  </div>

</ng-template>
<!-- / CORPS DE L'EMAIL INTERAC -->

<!-- ── CONFIRMATION DE CRÉDIT INTERAC ── -->
<ng-template #templateInteracCreditConfirm>
  <div class="modal-header mtx-modal__head mtx-modal__head--success">
    <h4 class="modal-title mtx-modal__title">Confirm Interac Credit</h4>
    <button type="button" class="close mtx-modal__close" aria-label="Close" (click)="cancelInteracCredit()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body" *ngIf="pendingInteracCredit">
    <p>The following operation will be performed:</p>
    <ul class="mtx-list">
      <li><strong>Sender:</strong> {{selectedInteracEmail?.from}}</li>
      <li><strong>Subject:</strong> {{selectedInteracEmail?.subject}}</li>
      <li><strong>Extracted amount:</strong> {{pendingInteracCredit.amountStr}}</li>
      <li><strong>Wallet:</strong> {{pendingInteracCredit.wallet.usersEmail}}
        <span *ngIf="pendingInteracCredit.wallet.interacPhone">
          (interac: {{pendingInteracCredit.wallet.interacPhone}})
        </span>
      </li>
      <li><strong>Currency:</strong> {{pendingInteracCredit.wallet.currency}}</li>
      <li><strong>Current balance:</strong> {{pendingInteracCredit.wallet.amount}}</li>
      <li>
        <strong>New balance after credit:</strong>
        {{pendingInteracCredit.newBalance}}
      </li>
    </ul>
  </div>

  <div class="modal-footer">
    <button type="button" class="mtx-btn mtx-btn--success" (click)="confirmInteracCredit()">
      Confirm &amp; Credit
    </button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="cancelInteracCredit()">
      Cancel
    </button>
  </div>
</ng-template>
<!-- / CONFIRMATION DE CRÉDIT INTERAC -->

<!-- ── CONFIRMATION DE CONTRE-PASSATION INTERAC ── -->
<ng-template #templateInteracReversalConfirm>
  <div class="modal-header mtx-modal__head mtx-modal__head--brand">
    <h4 class="modal-title mtx-modal__title">Reverse Interac Credit</h4>
    <button type="button" class="close mtx-modal__close" aria-label="Close" (click)="cancelInteracReversal()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body" *ngIf="pendingInteracReversal">
    <p>
      The email is currently <strong>processed</strong>. Moving it to
      <strong>{{pendingInteracReversal.targetStatus | uppercase}}</strong> will
      debit the previously credited amount from the wallet.
    </p>
    <ul class="mtx-list">
      <li><strong>Sender:</strong> {{selectedInteracEmail?.from}}</li>
      <li><strong>Subject:</strong> {{selectedInteracEmail?.subject}}</li>
      <li><strong>Extracted amount:</strong> {{pendingInteracReversal.amountStr}}</li>
      <li><strong>Wallet:</strong> {{pendingInteracReversal.wallet.usersEmail}}
        <span *ngIf="pendingInteracReversal.wallet.interacPhone">
          (interac: {{pendingInteracReversal.wallet.interacPhone}})
        </span>
      </li>
      <li><strong>Currency:</strong> {{pendingInteracReversal.wallet.currency}}</li>
      <li><strong>Current balance:</strong> {{pendingInteracReversal.wallet.amount}}</li>
      <li>
        <strong>New balance after debit:</strong>
        <span [ngClass]="{ 'mtx-negative': pendingInteracReversal.newBalance < 0 }">
          {{pendingInteracReversal.newBalance}}
        </span>
      </li>
    </ul>
    <div class="mtx-alert mtx-alert--warning" *ngIf="pendingInteracReversal.newBalance < 0">
      Warning: this will leave the wallet with a negative balance.
    </div>
  </div>

  <div class="modal-footer">
    <button type="button" class="mtx-btn mtx-btn--warning" (click)="confirmInteracReversal()">
      Confirm &amp; Debit
    </button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="cancelInteracReversal()">
      Cancel
    </button>
  </div>
</ng-template>
<!-- / CONFIRMATION DE CONTRE-PASSATION INTERAC -->

<!-- ── CONFIRMATION DE CRÉDIT MOMO ── -->
<ng-template #templateMomoCreditConfirm>
  <div class="modal-header mtx-modal__head mtx-modal__head--success">
    <h4 class="modal-title mtx-modal__title">Confirm MoMo Credit</h4>
    <button type="button" class="close mtx-modal__close" aria-label="Close" (click)="cancelMomoCredit()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body" *ngIf="pendingMomoCredit">
    <p>The following operation will be performed:</p>
    <ul class="mtx-list">
      <li><strong>Operator:</strong> {{selectedMomoDeposit?.operator}}</li>
      <li><strong>Transaction ID:</strong> {{selectedMomoDeposit?.transactionId}}</li>
      <li><strong>Declared amount:</strong> {{pendingMomoCredit.amount | number}} {{pendingMomoCredit.wallet.currency || 'XAF'}}</li>
      <li><strong>Wallet:</strong> {{pendingMomoCredit.wallet.usersEmail}}
        <span *ngIf="pendingMomoCredit.wallet.momoCode">
          (code: {{pendingMomoCredit.wallet.momoCode}})
        </span>
      </li>
      <li><strong>Sender phone:</strong> {{selectedMomoDeposit?.senderPhone || '—'}}</li>
      <li><strong>Current balance:</strong> {{pendingMomoCredit.wallet.amount | number}}</li>
      <li>
        <strong>New balance after credit:</strong>
        {{pendingMomoCredit.newBalance | number}}
      </li>
      <li *ngIf="selectedMomoDeposit?.note">
        <strong>Customer note:</strong> {{selectedMomoDeposit?.note}}
      </li>
    </ul>
    <p class="mtx-modal__lead">
      Make sure you have verified the transaction in your MTN/Orange MoMo statement before approving.
    </p>
  </div>

  <div class="modal-footer">
    <button type="button" class="mtx-btn mtx-btn--success" (click)="confirmMomoCredit()">
      Confirm &amp; Credit
    </button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="cancelMomoCredit()">
      Cancel
    </button>
  </div>
</ng-template>
<!-- / CONFIRMATION DE CRÉDIT MOMO -->

<!-- ── CONFIRMATION DE REJET MOMO ── -->
<ng-template #templateMomoRejectConfirm>
  <div class="modal-header mtx-modal__head mtx-modal__head--danger">
    <h4 class="modal-title mtx-modal__title">Reject MoMo Deposit</h4>
    <button type="button" class="close mtx-modal__close" aria-label="Close" (click)="cancelMomoReject()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body" *ngIf="selectedMomoDeposit">
    <div class="mtx-alert mtx-alert--warning" *ngIf="selectedMomoDeposit.status === 'approved'">
      <strong>Warning:</strong> this deposit was previously approved. Rejecting it will
      <strong>debit</strong> {{selectedMomoDeposit.amount | number}} {{selectedMomoDeposit.currency || 'XAF'}}
      from the wallet to reverse the credit.
    </div>
    <ul class="mtx-list">
      <li><strong>Operator:</strong> {{selectedMomoDeposit.operator}}</li>
      <li><strong>Transaction ID:</strong> {{selectedMomoDeposit.transactionId}}</li>
      <li><strong>Amount:</strong> {{selectedMomoDeposit.amount | number}} {{selectedMomoDeposit.currency || 'XAF'}}</li>
      <li><strong>Wallet:</strong> {{selectedMomoDeposit.walletEmail}}</li>
    </ul>
    <div class="mtx-field">
      <label class="mtx-field__label" for="momoRejectReason">Reason (optional):</label>
      <textarea id="momoRejectReason" class="mtx-input" rows="2"
                [(ngModel)]="momoRejectReason"
                placeholder="e.g. transaction not found in MTN statement"></textarea>
    </div>
  </div>

  <div class="modal-footer">
    <button type="button" class="mtx-btn mtx-btn--danger" (click)="confirmMomoReject()">
      Confirm Reject
    </button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="cancelMomoReject()">
      Cancel
    </button>
  </div>
</ng-template>
<!-- / CONFIRMATION DE REJET MOMO -->

<!-- ── CONFIRMATION DE CRÉDIT BANCAIRE ── -->
<ng-template #templateBankCreditConfirm>
  <div class="modal-header mtx-modal__head mtx-modal__head--success">
    <h4 class="modal-title mtx-modal__title">Confirmer le crédit</h4>
    <button type="button" class="close mtx-modal__close" aria-label="Fermer" (click)="cancelBankCredit()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body" *ngIf="pendingBankCredit">
    <p>L'opération suivante va être effectuée :</p>
    <ul class="mtx-list">
      <li><strong>Pays :</strong> {{selectedBankDeposit?.countryCode}}</li>
      <li><strong>Référence du virement :</strong> {{selectedBankDeposit?.reference}}</li>
      <li><strong>Montant déclaré :</strong> {{pendingBankCredit.amount | number}} {{pendingBankCredit.currency}}</li>
      <li><strong>Compte client :</strong> {{pendingBankCredit.email}}</li>
      <li><strong>Émetteur déclaré :</strong> {{selectedBankDeposit?.senderName || '—'}}</li>
      <li *ngIf="selectedBankDeposit?.note">
        <strong>Note du client :</strong> {{selectedBankDeposit?.note}}
      </li>
    </ul>
    <p class="mtx-modal__lead">
      Le solde n'est pas affiché ici : il est lu et mis à jour par le serveur dans la même
      transaction que l'approbation, à partir du portefeuille rattaché à ce dépôt. Un montant
      calculé dans ce navigateur pourrait diverger de celui qui sera réellement crédité.
    </p>
    <div class="mtx-alert mtx-alert--warning">
      <strong>Avant d'approuver :</strong> confirmez que le virement de
      {{pendingBankCredit.amount | number}} {{pendingBankCredit.currency}} portant la référence
      « {{selectedBankDeposit?.reference}} » figure bien sur le relevé du compte de réception.
      Le crédit est immédiat et ne peut pas être annulé depuis cet écran.
    </div>
  </div>

  <div class="modal-footer">
    <button type="button" class="mtx-btn mtx-btn--success" (click)="confirmBankCredit()">
      Confirmer &amp; créditer
    </button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="cancelBankCredit()">
      Annuler
    </button>
  </div>
</ng-template>
<!-- / CONFIRMATION DE CRÉDIT BANCAIRE -->

<!-- ── CONFIRMATION DE REJET BANCAIRE ── -->
<ng-template #templateBankRejectConfirm>
  <div class="modal-header mtx-modal__head mtx-modal__head--danger">
    <h4 class="modal-title mtx-modal__title">Rejeter le virement</h4>
    <button type="button" class="close mtx-modal__close" aria-label="Fermer" (click)="cancelBankReject()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body" *ngIf="selectedBankDeposit">
    <ul class="mtx-list">
      <li><strong>Pays :</strong> {{selectedBankDeposit.countryCode}}</li>
      <li><strong>Référence :</strong> {{selectedBankDeposit.reference}}</li>
      <li><strong>Montant :</strong> {{selectedBankDeposit.amount | number}} {{selectedBankDeposit.currency}}</li>
      <li><strong>Compte client :</strong> {{selectedBankDeposit.walletEmail}}</li>
    </ul>
    <div class="mtx-field">
      <label class="mtx-field__label" for="bankRejectReason">Motif (visible par le client) :</label>
      <textarea id="bankRejectReason" class="mtx-input" rows="2"
                [(ngModel)]="bankRejectReason"
                placeholder="Ex : aucun virement portant cette référence sur le relevé"></textarea>
    </div>
    <p class="mtx-modal__lead">
      Un rejet ne débite rien : seul un dépôt <em>en attente</em> peut être rejeté. Un dépôt déjà
      crédité doit être corrigé depuis l'onglet Portefeuilles, pour que le solde garde une seule
      source de vérité.
    </p>
  </div>

  <div class="modal-footer">
    <button type="button" class="mtx-btn mtx-btn--danger" (click)="confirmBankReject()">
      Confirmer le rejet
    </button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="cancelBankReject()">
      Annuler
    </button>
  </div>
</ng-template>
<!-- / CONFIRMATION DE REJET BANCAIRE -->

<!-- ── CORPS DU SIGNALEMENT ── -->
<ng-template #templateIssueBody>

  <div class="modal-header mtx-modal__head mtx-modal__head--danger">
    <h4 class="modal-title mtx-modal__title">Issue Report Details</h4>
    <button type="button" class="close mtx-modal__close" aria-label="Close" (click)="modalRef.hide()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body" *ngIf="selectedIssue">
    <div class="row">
      <div class="col-md-6">
        <p><strong>From:</strong> {{selectedIssue.senderName}} &lt;{{selectedIssue.from}}&gt;</p>
        <p><strong>Subject:</strong> {{selectedIssue.subject}}</p>
      </div>
      <div class="col-md-6">
        <p><strong>Date:</strong> {{selectedIssue.date | date:'medium'}}</p>
        <p><strong>Status:</strong>
          <span class="mtx-status" [ngClass]="{
            'mtx-status--ok': selectedIssue.status === 'resolved',
            'mtx-status--ko': selectedIssue.status === 'ignored',
            'mtx-status--wait': selectedIssue.status === 'new'
          }">
            {{selectedIssue.status | uppercase}}
          </span>
        </p>
      </div>
    </div>

    <hr class="mtx-modal__rule">

    <div class="mtx-pre">{{selectedIssue.body}}</div>
  </div>

  <div class="modal-footer">
    <button type="button" class="mtx-btn mtx-btn--success" (click)="markIssueResolved(); modalRef.hide();"
            *ngIf="selectedIssue?.status !== 'resolved'">
      Mark Resolved
    </button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="modalRef.hide()">Close</button>
  </div>

</ng-template>
<!-- / CORPS DU SIGNALEMENT -->

<!-- ── CORPS DU SIGNALEMENT DE DÉPÔT ── -->
<ng-template #templateDepositIssueBody>

  <div class="modal-header mtx-modal__head mtx-modal__head--violet">
    <h4 class="modal-title mtx-modal__title">Deposit Issue Report</h4>
    <button type="button" class="close mtx-modal__close" aria-label="Close" (click)="modalRef.hide()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body" *ngIf="selectedDepositIssue">
    <div class="row">
      <div class="col-md-6">
        <p><strong>User:</strong> {{selectedDepositIssue.userEmail}}</p>
        <p><strong>Type:</strong>
          {{selectedDepositIssue.depositType === 'interac' ? 'INTERAC e-Transfer' : 'Mobile Money'}}
          ({{selectedDepositIssue.countryCode}})
        </p>
        <p><strong>Sender contact:</strong>
          {{selectedDepositIssue.senderContact || '(not set)'}}
        </p>
      </div>
      <div class="col-md-6">
        <p><strong>Date:</strong> {{selectedDepositIssue.date | date:'medium'}}</p>
        <p><strong>Attachments:</strong> {{selectedDepositIssue.attachmentCount}} (check support inbox)</p>
        <p><strong>Status:</strong>
          <span class="mtx-status" [ngClass]="{
            'mtx-status--ok': selectedDepositIssue.status === 'resolved',
            'mtx-status--ko': selectedDepositIssue.status === 'ignored',
            'mtx-status--wait': selectedDepositIssue.status === 'new'
          }">
            {{selectedDepositIssue.status | uppercase}}
          </span>
        </p>
      </div>
    </div>

    <hr class="mtx-modal__rule">

    <h6 class="mtx-modal__subtitle">Message</h6>
    <div class="mtx-pre">{{selectedDepositIssue.message}}</div>
  </div>

  <div class="modal-footer">
    <button type="button" class="mtx-btn mtx-btn--success" (click)="markDepositIssueResolved(); modalRef.hide();"
            *ngIf="selectedDepositIssue?.status !== 'resolved'">
      Mark Resolved
    </button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="markDepositIssueIgnored(); modalRef.hide();"
            *ngIf="selectedDepositIssue?.status !== 'ignored'">
      Ignore
    </button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="modalRef.hide()">Close</button>
  </div>

</ng-template>
<!-- / CORPS DU SIGNALEMENT DE DÉPÔT -->

<!-- ── DÉTAIL DU POST MARKETPLACE ── -->
<ng-template #templatePostDetail>

  <div class="modal-header mtx-modal__head mtx-modal__head--violet">
    <h4 class="modal-title mtx-modal__title">Marketplace Post &amp; Proposition</h4>
    <button type="button" class="close mtx-modal__close" aria-label="Close" (click)="modalRef.hide()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body">
    <div *ngIf="loadingMarketplacePost" class="mtx-modal__loading">
      Loading linked post…
    </div>

    <div *ngIf="!loadingMarketplacePost && selectedMarketplacePost">
      <!-- POST -->
      <h6 class="mtx-modal__subtitle mtx-modal__subtitle--violet">Post</h6>
      <div class="row">
        <div class="col-md-6">
          <p><strong>Post owner:</strong> {{ selectedMarketplacePost.userEmail }}</p>
          <p><strong>Name:</strong> {{ selectedMarketplacePost.name }}</p>
          <p><strong>Phone:</strong> {{ selectedMarketplacePost.telephone }}</p>
        </div>
        <div class="col-md-6">
          <p><strong>Sender flag:</strong> {{ selectedMarketplacePost.senderFlag }}</p>
          <p><strong>Receiver flag:</strong> {{ selectedMarketplacePost.receiverFlag }}</p>
          <p><strong>Rate:</strong>
            <ng-container *ngFor="let kv of selectedMarketplacePost.rate | keyvalue">
              {{ kv.key }} → {{ kv.value }}
            </ng-container>
          </p>
          <p><strong>Status:</strong> {{ selectedMarketplacePost.status ? 'Closed' : 'Open' }}</p>
        </div>
      </div>
      <div class="mtx-pre mtx-pre--compact">{{ selectedMarketplacePost.message }}</div>

      <!-- PROPOSITION ACCEPTÉE -->
      <h6 class="mtx-modal__subtitle mtx-modal__subtitle--violet">Accepted proposition</h6>
      <div *ngIf="selectedMarketplaceProposition" class="row">
        <div class="col-md-6">
          <p><strong>Proposer:</strong> {{ selectedMarketplaceProposition.propositionEmail }}</p>
          <p><strong>Amount:</strong> {{ selectedMarketplaceProposition.amount }}</p>
          <p><strong>Send:</strong> {{ selectedMarketplaceProposition.amountSend }}</p>
          <p><strong>Received:</strong> {{ selectedMarketplaceProposition.amountReceived }}</p>
        </div>
        <div class="col-md-6">
          <p><strong>Status:</strong> {{ selectedMarketplaceProposition.status ? 'Accepted' : 'Pending' }}</p>
          <p><strong>Progress:</strong> {{ selectedMarketplaceProposition.progressBarStatus || '—' }}</p>
          <p><strong>Post-side txn:</strong> {{ selectedMarketplaceProposition.idPostTransaction || '—' }}</p>
          <p><strong>Proposition-side txn:</strong> {{ selectedMarketplaceProposition.idPropositionTransaction || '—' }}</p>
        </div>
      </div>
      <p *ngIf="!selectedMarketplaceProposition" class="mtx-muted">
        Could not match the proposition on this post (it may have been removed).
      </p>

      <!-- TRANSACTIONS LIÉES -->
      <h6 class="mtx-modal__subtitle mtx-modal__subtitle--violet">Linked transactions</h6>
      <div class="row">
        <div class="col-md-6">
          <div class="mtx-subcard">
            <p><strong>Post owner's transaction</strong></p>
            <ng-container *ngIf="selectedMarketplaceTransactions.postSide; else noPostSide">
              <p>Id: {{ selectedMarketplaceTransactions.postSide.id }}</p>
              <p>User: {{ selectedMarketplaceTransactions.postSide.userEmail }}</p>
              <p>Status: {{ selectedMarketplaceTransactions.postSide.status }}</p>
              <p>Send: {{ selectedMarketplaceTransactions.postSide.amountSend }}</p>
              <p>Receive: {{ selectedMarketplaceTransactions.postSide.amountReceive }}</p>
            </ng-container>
            <ng-template #noPostSide><p class="mtx-muted">Not found in current list.</p></ng-template>
          </div>
        </div>
        <div class="col-md-6">
          <div class="mtx-subcard">
            <p><strong>Proposition owner's transaction</strong></p>
            <ng-container *ngIf="selectedMarketplaceTransactions.propositionSide; else noPropSide">
              <p>Id: {{ selectedMarketplaceTransactions.propositionSide.id }}</p>
              <p>User: {{ selectedMarketplaceTransactions.propositionSide.userEmail }}</p>
              <p>Status: {{ selectedMarketplaceTransactions.propositionSide.status }}</p>
              <p>Send: {{ selectedMarketplaceTransactions.propositionSide.amountSend }}</p>
              <p>Receive: {{ selectedMarketplaceTransactions.propositionSide.amountReceive }}</p>
            </ng-container>
            <ng-template #noPropSide><p class="mtx-muted">Not found in current list.</p></ng-template>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="modal-footer">
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="modalRef.hide()">Close</button>
  </div>

</ng-template>
<!-- / DÉTAIL DU POST MARKETPLACE -->

<!-- ═══════════ DÉTAIL D'UN MOUVEMENT D'ARGENT ═══════════ -->
<!-- À la RACINE du gabarit, avec les autres modales : déclarée dans le bloc
     *ngIf de l'onglet, la référence @ViewChild serait indéfinie au premier clic. -->
<ng-template #templateMoneyEventDetail>
  <div class="modal-header mtx-modal__head" [ngClass]="moneyEventHeadClass()">
    <div class="mtx-modal__head-group">
      <h4 class="modal-title mtx-modal__title">
        {{moneyActionLabel(selectedMoneyEvent?.action)}}
      </h4>
      <span class="mtx-modal__subtle">
        {{moneyOutcomeLabel(selectedMoneyEvent?.outcome)}}
        · {{selectedMoneyEvent?.createdAtIso | date:'dd/MM/yy HH:mm'}}
      </span>
    </div>
    <button type="button" class="close mtx-modal__close" aria-label="Fermer" (click)="modalRef.hide()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body" *ngIf="selectedMoneyEvent">

    <!-- L'anomalie d'abord : c'est la raison du clic. -->
    <div class="mtx-alert mtx-alert--warning" *ngIf="selectedMoneyEvent.attribution === 'unattributed'">
      Ce solde a changé sans qu'aucune fonction serveur ne le signe — typiquement
      une modification manuelle depuis le tableau de bord.
    </div>
    <div class="mtx-alert mtx-alert--warning" *ngIf="selectedMoneyEvent.source === 'client'">
      Signalement du client : une déclaration, pas un fait. L'application dit
      « {{selectedMoneyEvent.client?.phase}} » ; le serveur répond
      « {{moneyTruthLabel(selectedMoneyEvent.serverTruth)}} ».
      Signalé {{selectedMoneyEvent.reportCount || 1}} fois.
    </div>

    <div class="row">
      <div class="col-md-6">
        <p><strong>Source :</strong> {{moneySourceLabel(selectedMoneyEvent.source)}}</p>
        <p><strong>Méthode :</strong> {{selectedMoneyEvent.method || '—'}}</p>
        <p><strong>Montant :</strong> {{selectedMoneyEvent.amountDisplay || '—'}}</p>
      </div>
      <div class="col-md-6">
        <p><strong>Client :</strong> {{selectedMoneyEvent.userEmail || '—'}}</p>
        <p><strong>Contrepartie :</strong> {{selectedMoneyEvent.counterpartyEmail || '—'}}</p>
        <p><strong>Déclenché par :</strong>
          {{selectedMoneyEvent.actorEmail || '—'}} ({{selectedMoneyEvent.actorRole}})</p>
      </div>
    </div>

    <ng-container *ngIf="selectedMoneyEvent.failureReason || selectedMoneyEvent.refusalCategory !== 'none'">
      <hr class="mtx-modal__rule">
      <h5 class="mtx-modal__subtitle">Pourquoi cela n'a pas abouti</h5>
      <p>
        <strong>{{refusalLabel(selectedMoneyEvent.refusalCategory)}}</strong>
        <span class="mtx-code" *ngIf="selectedMoneyEvent.failureCode">
          {{selectedMoneyEvent.failureCode}}</span>
      </p>
      <!-- .mtx-pre : défilable, pre-wrap, word-break — le message serveur peut
           faire 300 caractères et doit rester lisible en entier. -->
      <div class="mtx-pre" *ngIf="selectedMoneyEvent.failureReason">{{selectedMoneyEvent.failureReason}}</div>
    </ng-container>

    <hr class="mtx-modal__rule">
    <h5 class="mtx-modal__subtitle">Mouvements de solde</h5>

    <div class="row" *ngIf="selectedMoneyEvent.legCount">
      <div class="col-md-6" *ngFor="let leg of selectedMoneyEvent.legs">
        <div class="mtx-subcard">
          <div class="mtx-md__tx-head">
            <strong>{{leg.role}}</strong>
            <span class="mtx-status"
                  [ngClass]="leg.direction === 'credit' ? 'mtx-status--ok' : 'mtx-status--ko'">
              {{leg.direction === 'credit' ? 'Crédit' : 'Débit'}}
            </span>
          </div>
          <div class="mtx-md__kv"><span>Client</span><b>{{leg.email || '—'}}</b></div>
          <div class="mtx-md__kv"><span>Montant</span>
            <b>{{leg.amount | number:'1.0-3'}} {{leg.currency}}</b></div>
          <div class="mtx-md__kv"><span>Solde avant</span>
            <b>{{leg.balanceBefore | number:'1.0-3'}}</b></div>
          <div class="mtx-md__kv"><span>Solde après</span>
            <b>{{leg.balanceAfter | number:'1.0-3'}}</b></div>
          <div class="mtx-md__kv" *ngIf="leg.walletId"><span>Portefeuille</span>
            <b>{{leg.walletId}}</b></div>
        </div>
      </div>
    </div>
    <!-- Une absence de jambe est elle-même un diagnostic : un « Réussi » qui n'a
         déplacé aucun solde est une anomalie. -->
    <p class="mtx-muted" *ngIf="!selectedMoneyEvent.legCount">
      Aucun mouvement de solde attaché à cet événement.
    </p>

    <hr class="mtx-modal__rule">
    <p><strong>Corrélation :</strong>
      <span class="mtx-code">{{selectedMoneyEvent.correlationId || '—'}}</span></p>
    <ul class="mtx-list" *ngIf="selectedMoneyEventArtifacts.length">
      <li *ngFor="let a of selectedMoneyEventArtifacts">
        <strong>{{a.key}} :</strong> {{a.value}}
      </li>
    </ul>
  </div>

  <div class="modal-footer">
    <button type="button" class="mtx-btn mtx-btn--primary"
            *ngIf="selectedMoneyEvent?.correlationId"
            (click)="showMoneyEventStory(selectedMoneyEvent.correlationId)">
      Voir tout l'historique de cette transaction
    </button>
    <button type="button" class="mtx-btn mtx-btn--ghost" (click)="modalRef.hide()">Fermer</button>
  </div>
</ng-template>
